// Based on:
//   Kevin Kwok https://github.com/antimatter15/splat
//   Quadjr https://github.com/quadjr/aframe-gaussian-splatting
// Adapted by:
//   Paul Henschel twitter.com/0xca0a

import * as THREE from 'three'

// Pixel-shader test switches (2026-10-22): ?fragHighp=1 — full precision
// again; ?fragCut=0 — blend near-transparent quad corners again. For telling
// which of the two cheaper-pixel changes causes a visible difference.
const _fragParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams()
const FRAG_HIGHP = _fragParams.get('fragHighp') === '1'
const FRAG_CUT = _fragParams.get('fragCut') !== '0'
// sRGB output encoding once per splat in the vertex shader (2026-10-23), not
// per pixel: the colour is the same across a splat, and the pixel shader runs
// for at least a 2×2 block per splat however small — three pow() per pixel
// were ~10% of the frame (M3 Pro, WebKit, 1.9M splats). Visually identical
// (pixel diff within run-to-run noise). WebGL only: the canvas output there
// is always sRGB (captures draw to the canvas too). ?vsrgb=0 = per pixel.
const VS_SRGB = _fragParams.get('vsrgb') !== '0'
// ?edgeCut=<1/n> (test): opacity below which a splat's edge is cut (quad size
// and per-pixel discard). Default 1/255.
const EDGE_CUT = (() => { const v = parseFloat(_fragParams.get('edgeCut') || ''); return Number.isFinite(v) && v > 0 && v < 1 ? v : 1 / 255 })()
// ?aa=<px²> (test): anti-aliased footprint — widen every splat by this much
// (instead of covClamp) and dim it by the same factor (Mip-Splatting's 2D
// filter), so sub-pixel needles stop shimmering as they move.
const AA_DILATION = (() => { const v = parseFloat(_fragParams.get('aa') || ''); return Number.isFinite(v) && v > 0 ? v : 0 })()
import * as React from 'react'
// WebGPU migration Stage 2 (?webgpu=1) — see buildGaussianSplatGeometry's
// own comment below for why this reuses three.js's shipped implementation
// instead of a from-scratch TSL rewrite. GaussianSplat.js itself pulls in
// three/webgpu + three/tsl (already accepted as part of Stage 1's bundle
// cost); GaussianSplatUtils.js is a small, renderer-agnostic pure-JS helper.
import { GaussianSplat } from 'three/addons/objects/GaussianSplat.js'
import { createGaussianSplatGeometry, createPackedSphericalHarmonicsBand } from 'three/addons/utils/GaussianSplatUtils.js'
import { fetchWithModelCache } from './utils/modelCache'
import { accessUrl, chunkCacheCap, enforceCap, nearCameraFiles, networkAllowsPrefetch, openTreeCache, planPreload, preloadFiles, setAccessQuery } from './utils/lodChunkCache'
import { loadTrace } from './utils/loadTrace'
// Streamed-SOG (Phase 1+2, dev-gated — see
// /Users/uldis.liepins/.claude/plans/witty-watching-star.md). Purely
// additive: nothing below this import is read by the existing
// SplatLoader.load() path.
import { SplatPool } from './three/splatPool'
import { createSplatNodeMaterial } from './three/splatNodeMaterial'
import { createSplatGpuSort } from './three/splatGpuSort'
import { vec4 as tslVec4, sRGBTransferOETF } from 'three/tsl'
// ?exactPack=1 (streamed path only — see loadStreamed's opts.exactPack):
// exact-size allocator, same public API as SplatPool, no power-of-two
// bucket-rounding waste. See splatPoolExact.js's own header comment.
import { SplatPoolExact } from './three/splatPoolExact'
import { parseLodMeta, SplatOctree } from './three/splatOctree'

export type SplatMaterialType = {
  alphaTest?: number
  alphaHash?: boolean
  centerAndScaleTexture?: THREE.DataTexture
  covAndColorTexture?: THREE.DataTexture
  viewport?: THREE.Vector2
  focal?: number
  hasSH?: number
  shDegree?: number
  shTexture?: THREE.DataTexture | null
  covClamp?: number
}

export type TargetMesh = THREE.Mesh<THREE.InstancedBufferGeometry, THREE.ShaderMaterial & SplatMaterialType> & {
  ready: boolean
  sorted: boolean
  pm: THREE.Matrix4
  vm1: THREE.Matrix4
  vm2: THREE.Matrix4
  viewport: THREE.Vector4
  // Sort-upload throttle counter — see update()'s SORT_THROTTLE_FRAMES.
  sortSkipCount?: number
}

export type SharedState = {
  url: string
  // The S3 object's current ETag, from the same canView()-gated getModel
  // call that produced `url` — lets load() below reuse a local cached copy
  // instead of re-downloading, without weakening access control at all
  // (see utils/modelCache.js for the full reasoning).
  etag?: string
  gl: THREE.WebGLRenderer
  worker: Worker
  manager: THREE.LoadingManager
  stream: ReadableStreamDefaultReader<Uint8Array>
  loading: boolean
  loaded: boolean
  // Set by lazyLoad() if the byte stream itself fails partway through (e.g.
  // a network interruption) — lazyLoad still sets loaded=true unconditionally
  // right after (a pre-existing behavior kept as-is: whatever partial data
  // was received by that point still gets pushed to the GPU rather than
  // discarded), so callers that need to know "did this genuinely finish
  // cleanly" must check loadError is unset, not just loaded === true.
  loadError?: unknown
  loadedVertexCount: number
  rowLength: number
  maxVertexes: number
  // Splat-count percentage (100 | 75 | 50). Below 100, lazyLoad keeps a
  // uniform spatial fraction so the model renders whole at lower density.
  splatDensity?: number
  chunkSize: number
  totalDownloadBytes: number
  numVertices: number
  bufferTextureWidth: number
  bufferTextureHeight: number
  centerAndScaleData: Float32Array
  covAndColorData: Uint32Array
  covAndColorTexture: THREE.DataTexture
  centerAndScaleTexture: THREE.DataTexture
  // Spherical harmonics — texture rows per gaussian: 3 (degree-1) or 12 (degree-3)
  sh: boolean
  hasSH: boolean
  shDegree: number
  // fp16 half-float staging for the RGBA16F SH texture (see setupSharedTextures)
  shData: Uint16Array | null
  shTexture: THREE.DataTexture | null
  // PLY deferred loading
  plyBuffer: ArrayBuffer | null
  plyMeta: { headerLength: number; rowLength: number; propOffsets: Record<string, number>; propTypes: Record<string, string>; fRestScale: number } | null
  // SOG deferred loading
  sogDataTable: any | null
  connect(target: TargetMesh): () => void
  update(target: TargetMesh, camera: THREE.Camera, hashed: boolean): void
  onProgress?: (event: ProgressEvent) => void
  // Real progress of the post-download stages (0-1): 'decode' = SOG WASM
  // decode passes (worker), 'push' = per-splat texture/matrix build. Feeds
  // the loading ring (utils/loadProgress.js) — no time estimates.
  onStageProgress?: (stage: 'decode' | 'push' | 'build', fraction: number) => void
  // Set by Splat under WebGPU: sorting happens on the GPU (splatGpuSort.js),
  // so the loaders skip building/posting the CPU sort worker's matrices.
  gpuSort?: boolean
}

// Inlined from @react-three/drei's shaderMaterial (core/shaderMaterial.js) —
// this file never uses R3F's JSX reconciler (Splat is instantiated directly
// via `new Splat(...)`, not through <Canvas>/JSX), so pulling in the whole
// @react-three/fiber + @react-three/drei packages just for this one small
// factory function isn't worth it — worse, their bundled internals
// reference three.js's old `*Encoding` enums (LinearEncoding etc.), which
// don't exist any more as of three 0.186 and broke the build outright once
// three was upgraded. Behavior identical to drei's version.
function shaderMaterial(uniforms: Record<string, any>, vertexShader: string, fragmentShader: string) {
  return class extends THREE.ShaderMaterial {
    constructor(parameters: Record<string, any> = {}) {
      const entries = Object.entries(uniforms)
      super({
        uniforms: entries.reduce((acc: Record<string, any>, [name, value]) => {
          acc[name] = { value }
          return acc
        }, {}),
        vertexShader,
        fragmentShader,
      })
      entries.forEach(([name]) => Object.defineProperty(this, name, {
        get: () => this.uniforms[name].value,
        set: (v) => { this.uniforms[name].value = v },
      }))
      Object.assign(this, parameters)
    }
  }
}

const SplatMaterial = /* @__PURE__ */ shaderMaterial(
  {
    alphaTest: 0,
    viewport: /* @__PURE__ */ new THREE.Vector2(1980, 1080),
    focal: 1000.0,
    centerAndScaleTexture: null,
    covAndColorTexture: null,
    hasSH: 0,
    shDegree: 3,
    shLayout: 3,
    shTexture: null,
    covClamp: 0.1,
    density: 1.0,
  },
  /*glsl*/ `
    precision highp sampler2D;
    precision highp usampler2D;
    // Varyings at 16-bit, 2 components for the position (z was never read):
    // Apple's tile-based GPUs store every corner's outputs to memory before
    // drawing pixels — ×4 corners × every splat, every frame — so this halves
    // that traffic. The pixel shader already works at 16-bit (FRAG_HIGHP).
    out ${FRAG_HIGHP ? '' : 'mediump '}vec4 vColor;
    out ${FRAG_HIGHP ? '' : 'mediump '}vec2 vPosition;
    uniform vec2 viewport;
    uniform float focal;
    attribute uint splatIndex;
    uniform sampler2D centerAndScaleTexture;
    uniform usampler2D covAndColorTexture;
    uniform int hasSH;
    // shLayout: the degree the SH texture was built for (3 / 9 / 12 rows
    // per splat, fixed at load). shDegree: the degree evaluated, live, at
    // most shLayout — 0 = base colour only.
    uniform int shDegree;
    uniform int shLayout;
    uniform sampler2D shTexture;
    uniform float covClamp;
    // Live splat-count reduction (static models): draw only this share of
    // the loaded splats — see the density check in main().
    uniform float density;

    // SH basis constants (match 3DGS eval_sh.py)
    const float SH_C0  = 0.28209479177387814;
    const float SH_C1  = 0.4886025119029199;
    const float SH_C2_0 =  1.0925484305920792;
    const float SH_C2_1 = -1.0925484305920792;
    const float SH_C2_2 =  0.31539156525252005;
    const float SH_C2_3 = -1.0925484305920792;
    const float SH_C2_4 =  0.5462742152960396;
    const float SH_C3_0 = -0.5900435899266435;
    const float SH_C3_1 =  2.890611442640554;
    const float SH_C3_2 = -0.4570457994644658;
    const float SH_C3_3 =  0.3731763325901154;
    const float SH_C3_4 = -0.4570457994644658;
    const float SH_C3_5 =  1.445305721320277;
    const float SH_C3_6 = -0.5900435899266435;

    float evalSH(vec4 r0, vec4 r1, vec4 r2, vec4 r3, vec3 d) {
      float x = d.x, y = d.y, z = d.z;
      float xx = x*x, yy = y*y, zz = z*z;
      float xy = x*y, xz = x*z, yz = y*z;

      float v = SH_C0 * r0.x;
      // degree 1
      v += SH_C1 * (-r0.y*y + r0.z*z - r0.w*x);
      // degree 2
      v += SH_C2_0 * xy         * r1.x;
      v += SH_C2_1 * yz         * r1.y;
      v += SH_C2_2 * (2.0*zz - xx - yy) * r1.z;
      v += SH_C2_3 * xz         * r1.w;
      v += SH_C2_4 * (xx - yy)  * r2.x;
      // degree 3
      v += SH_C3_0 * y*(3.0*xx - yy)          * r2.y;
      v += SH_C3_1 * xy*z                      * r2.z;
      v += SH_C3_2 * y*(4.0*zz - xx - yy)      * r2.w;
      v += SH_C3_3 * z*(2.0*zz - 3.0*xx - 3.0*yy) * r3.x;
      v += SH_C3_4 * x*(4.0*zz - xx - yy)      * r3.y;
      v += SH_C3_5 * z*(xx - yy)               * r3.z;
      v += SH_C3_6 * x*(xx - 3.0*yy)           * r3.w;
      return v + 0.5;
    }

    // Degree-1+2 only (no degree-3 terms) - used when shDegree == 2
    float evalSH2(vec4 r0, vec4 r1, vec4 r2, vec3 d) {
      float x = d.x, y = d.y, z = d.z;
      float xx = x*x, yy = y*y, zz = z*z;
      float xy = x*y, xz = x*z, yz = y*z;

      float v = SH_C0 * r0.x;
      v += SH_C1 * (-r0.y*y + r0.z*z - r0.w*x);
      v += SH_C2_0 * xy         * r1.x;
      v += SH_C2_1 * yz         * r1.y;
      v += SH_C2_2 * (2.0*zz - xx - yy) * r1.z;
      v += SH_C2_3 * xz         * r1.w;
      v += SH_C2_4 * (xx - yy)  * r2.x;
      return v + 0.5;
    }

    vec2 unpackInt16(in uint value) {
      int v = int(value);
      int v0 = v >> 16;
      int v1 = (v & 0xFFFF);
      if((v & 0x8000) != 0) v1 |= 0xFFFF0000;
      return vec2(float(v1), float(v0));
    }

    void main () {
      // Skip splats past the density share (fixed per-splat hash, so the drawn set
      // is stable and spread evenly over the model) — same early-out as the
      // off-screen cull below, so a skipped splat costs almost nothing.
      if (density < 1.0) {
        uint h = splatIndex * 2654435761u;
        h ^= h >> 16;
        if (float(h & 0xFFFFFFu) / 16777216.0 >= density) {
          gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
          return;
        }
      }

      ivec2 texSize = textureSize(centerAndScaleTexture, 0);
      ivec2 texPos = ivec2(splatIndex%uint(texSize.x), splatIndex/uint(texSize.x));
      vec4 centerAndScaleData = texelFetch(centerAndScaleTexture, texPos, 0);
      vec4 center = vec4(centerAndScaleData.xyz, 1);
      vec4 camspace = modelViewMatrix * center;
      vec4 pos2d = projectionMatrix * camspace;

      float bounds = 1.2 * pos2d.w;
      if (pos2d.z < -pos2d.w || pos2d.x < -bounds || pos2d.x > bounds
        || pos2d.y < -bounds || pos2d.y > bounds) {
        gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
        return;
      }

      uvec4 covAndColorData = texelFetch(covAndColorTexture, texPos, 0);
      uint colorUint = covAndColorData.w;
      float alpha = float(colorUint >> uint(24)) / 255.0;
      // Invisible (alpha ≤ 1/255): culled here, before the covariance and SH
      // work — same result as culling it after them, which it used to.
      if (alpha * 255.0 <= 1.0) {
        gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
        return;
      }
      vec2 cov3D_M11_M12 = unpackInt16(covAndColorData.x) * centerAndScaleData.w;
      vec2 cov3D_M13_M22 = unpackInt16(covAndColorData.y) * centerAndScaleData.w;
      vec2 cov3D_M23_M33 = unpackInt16(covAndColorData.z) * centerAndScaleData.w;
      mat3 Vrk = mat3(
        cov3D_M11_M12.x, cov3D_M11_M12.y, cov3D_M13_M22.x,
        cov3D_M11_M12.y, cov3D_M13_M22.y, cov3D_M23_M33.x,
        cov3D_M13_M22.x, cov3D_M23_M33.x, cov3D_M23_M33.y
      );
      mat3 J = mat3(
        focal / camspace.z, 0., -(focal * camspace.x) / (camspace.z * camspace.z),
        0., focal / camspace.z, -(focal * camspace.y) / (camspace.z * camspace.z),
        0., 0., 0.
      );
      mat3 W = transpose(mat3(modelViewMatrix));
      mat3 T = W * J;
      mat3 cov = transpose(T) * Vrk * T;
      vec2 vCenter = vec2(pos2d) / pos2d.w;
      float diagonal1 = cov[0][0] + ${AA_DILATION > 0 ? AA_DILATION.toFixed(4) : 'covClamp'};
      float offDiagonal = cov[0][1];
      float diagonal2 = cov[1][1] + ${AA_DILATION > 0 ? AA_DILATION.toFixed(4) : 'covClamp'};
      ${AA_DILATION > 0 ? 'alpha *= sqrt(max(cov[0][0] * cov[1][1] - offDiagonal * offDiagonal, 0.0) / max(diagonal1 * diagonal2 - offDiagonal * offDiagonal, 1e-12));' : ''}
      float mid = 0.5 * (diagonal1 + diagonal2);
      float radius = length(vec2((diagonal1 - diagonal2) / 2.0, offDiagonal));
      float lambda1 = mid + radius;
      float lambda2 = max(mid - radius, 0.1);
      vec2 diagonalVector = normalize(vec2(offDiagonal, lambda1 - diagonal1));
      vec2 v1 = min(sqrt(2.0 * lambda1), 1024.0) * diagonalVector;
      vec2 v2 = min(sqrt(2.0 * lambda2), 1024.0) * vec2(diagonalVector.y, -diagonalVector.x);

      if (hasSH == 1) {
        // Gaussian centers stored as (px,-py,-pz); flip Y,Z back to 3DGS space for SH direction
        vec3 worldCenter = (modelMatrix * center).xyz;
        vec3 rd = normalize(worldCenter - cameraPosition);
        vec3 dir = vec3(rd.x, -rd.y, -rd.z);
        float dx = dir.x, dy = dir.y, dz = dir.z;
        int col = texPos.x;
        // Rows per colour channel in this texture's layout; a lower live
        // degree reads only the leading rows of each channel's block.
        int rpc = shLayout == 1 ? 1 : (shLayout == 2 ? 3 : 4);
        int baseRow = texPos.y * rpc * 3;
        int deg = min(shDegree, shLayout);

        float r, g, b;
        vec4 rr0 = texelFetch(shTexture, ivec2(col, baseRow), 0);
        vec4 gr0 = texelFetch(shTexture, ivec2(col, baseRow + rpc), 0);
        vec4 br0 = texelFetch(shTexture, ivec2(col, baseRow + 2 * rpc), 0);
        if (deg <= 0) {
          // Base colour only (SH degree 0), same colour handling as below
          r = SH_C0 * rr0.x + 0.5;
          g = SH_C0 * gr0.x + 0.5;
          b = SH_C0 * br0.x + 0.5;
        } else if (deg == 1) {
          // row 0 of each channel = [dc, d1x, d1y, d1z]
          r = SH_C0 * rr0.x + SH_C1 * (-rr0.y*dy + rr0.z*dz - rr0.w*dx) + 0.5;
          g = SH_C0 * gr0.x + SH_C1 * (-gr0.y*dy + gr0.z*dz - gr0.w*dx) + 0.5;
          b = SH_C0 * br0.x + SH_C1 * (-br0.y*dy + br0.z*dz - br0.w*dx) + 0.5;
        } else {
          vec4 rr1 = texelFetch(shTexture, ivec2(col, baseRow + 1), 0);
          vec4 rr2 = texelFetch(shTexture, ivec2(col, baseRow + 2), 0);
          vec4 gr1 = texelFetch(shTexture, ivec2(col, baseRow + rpc + 1), 0);
          vec4 gr2 = texelFetch(shTexture, ivec2(col, baseRow + rpc + 2), 0);
          vec4 br1 = texelFetch(shTexture, ivec2(col, baseRow + 2 * rpc + 1), 0);
          vec4 br2 = texelFetch(shTexture, ivec2(col, baseRow + 2 * rpc + 2), 0);
          if (deg == 2) {
            r = evalSH2(rr0, rr1, rr2, dir);
            g = evalSH2(gr0, gr1, gr2, dir);
            b = evalSH2(br0, br1, br2, dir);
          } else {
            vec4 rr3 = texelFetch(shTexture, ivec2(col, baseRow + 3), 0);
            vec4 gr3 = texelFetch(shTexture, ivec2(col, baseRow + rpc + 3), 0);
            vec4 br3 = texelFetch(shTexture, ivec2(col, baseRow + 2 * rpc + 3), 0);
            r = evalSH(rr0, rr1, rr2, rr3, dir);
            g = evalSH(gr0, gr1, gr2, gr3, dir);
            b = evalSH(br0, br1, br2, br3, dir);
          }
        }

        // SH evaluates to sRGB; linearize (proper piecewise) so colorspace_fragment round-trips correctly
        vec3 srgb = clamp(vec3(r, g, b), 0.0, 1.0);
        ${VS_SRGB ? 'vColor = vec4(srgb, alpha);' : `vec3 lin = mix(srgb / 12.92, pow((srgb + 0.055) / 1.055, vec3(2.4)), step(0.04045, srgb));
        vColor = vec4(lin, alpha);`}
      } else {
        vColor = vec4(
          float(colorUint & uint(0xFF)) / 255.0,
          float((colorUint >> uint(8)) & uint(0xFF)) / 255.0,
          float((colorUint >> uint(16)) & uint(0xFF)) / 255.0,
          alpha
        );
        ${VS_SRGB ? 'vColor.rgb = mix(vColor.rgb * 12.92, pow(vColor.rgb, vec3(1.0 / 2.4)) * 1.055 - 0.055, step(0.0031308, vColor.rgb));' : ''}
      }

      // Opacity-aware quad size (fill-rate saving, image-neutral) — same
      // as the WebGPU material (three/splatNodeMaterial.js): fragment alpha
      // is exp(-|p|^2)*alpha on the +-2 quad, so beyond |p| = sqrt(ln(255*alpha))
      // a splat adds < 1/255. Opaque splats keep the full quad; splats below
      // 1/255 at their peak aren't drawn.
      float extent = min(sqrt(max(log(alpha / ${EDGE_CUT.toFixed(8)}), 0.0)) * 0.5, 1.0);
      vec3 p = position * extent;
      vPosition = p.xy;
      gl_Position = vec4(
        vCenter
          + p.x * v2 / viewport * 2.0
          + p.y * v1 / viewport * 2.0, pos2d.z / pos2d.w, 1.0);
    }
    `,
  /*glsl*/ `
    // Half precision from here on (2026-10-22): colour, opacity and the
    // gaussian falloff (|p|² ≤ 4) need nothing more, and Apple GPUs run
    // 16-bit maths at twice the rate — pixel work is a large part of the
    // frame on an iPad. three's prefix above (its uniforms, shared with the
    // vertex shader) keeps its own default precision.
    ${FRAG_HIGHP ? '' : 'precision mediump float;'}
    #include <alphatest_pars_fragment>
    #include <alphahash_pars_fragment>
    in vec4 vColor;
    in vec2 vPosition;
    void main () {
      float A = -dot(vPosition, vPosition);
      if (A < -4.0) discard;
      float B = exp(A) * vColor.a;
      // The quad is square, the splat round: its corners (~21% of every
      // quad) add under 1/255 — skip them instead of blending nothing.
      ${FRAG_CUT ? `if (B < ${EDGE_CUT.toFixed(8)}) discard;` : ''}
      vec4 diffuseColor = vec4(vColor.rgb, B);
      #include <alphatest_fragment>
      #include <alphahash_fragment>
      gl_FragColor = diffuseColor;
      #include <tonemapping_fragment>
      ${VS_SRGB ? '' : `#include <${parseInt(THREE.REVISION.replace(/\D+/g, '')) >= 154 ? 'colorspace_fragment' : 'encodings_fragment'}>`}
    }
  `
)

function createWorker(self: any) {
  let matrices: Float32Array = null!
  let offset = 0

  function sortSplats(view: Float32Array, hashed: boolean = false, density: number = 1) {
    const vertexCount = matrices.length / 16
    const threshold = -0.0001
    let maxDepth = -Infinity
    let minDepth = Infinity
    const depthList = new Float32Array(vertexCount)
    const sizeList = new Int32Array(depthList.buffer)
    const validIndexList = new Int32Array(vertexCount)
    let validCount = 0
    for (let i = 0; i < vertexCount; i++) {
      // Live splat share: skip the splats the shader won't draw (the same
      // per-splat hash as SplatMaterial's density check), so lowering the
      // share also makes sorting cheaper.
      if (density < 1) {
        let h = Math.imul(i, 2654435761) >>> 0
        h = (h ^ (h >>> 16)) >>> 0
        if ((h & 0xFFFFFF) / 16777216 >= density) continue
      }
      const depth = view[0] * matrices[i * 16 + 12] + view[1] * matrices[i * 16 + 13] + view[2] * matrices[i * 16 + 14] + view[3]
      if (hashed || (depth < 0 && matrices[i * 16 + 15] > threshold * depth)) {
        depthList[validCount] = depth
        validIndexList[validCount] = i
        validCount++
        if (depth > maxDepth) maxDepth = depth
        if (depth < minDepth) minDepth = depth
      }
    }
    const depthInv = (256 * 256 - 1) / (maxDepth - minDepth)
    const counts0 = new Uint32Array(256 * 256)
    for (let i = 0; i < validCount; i++) {
      sizeList[i] = ((depthList[i] - minDepth) * depthInv) | 0
      counts0[sizeList[i]]++
    }
    const starts0 = new Uint32Array(256 * 256)
    for (let i = 1; i < 256 * 256; i++) starts0[i] = starts0[i - 1] + counts0[i - 1]
    const depthIndex = new Uint32Array(validCount)
    for (let i = 0; i < validCount; i++) depthIndex[starts0[sizeList[i]]++] = validIndexList[i]
    return depthIndex
  }

  self.onmessage = (e: {
    data: { method: string; length: number; key: string; view: Float32Array; matrices: Float32Array; hashed: boolean; density?: number }
  }) => {
    if (e.data.method == 'push') {
      if (offset === 0) matrices = new Float32Array(e.data.length)
      const new_matrices = new Float32Array(e.data.matrices)
      matrices.set(new_matrices, offset)
      offset += new_matrices.length
    } else if (e.data.method == 'sort') {
      if (matrices !== null) {
        const indices = sortSplats(new Float32Array(e.data.view), e.data.hashed, e.data.density ?? 1)
        // @ts-ignore
        // density echoed so the mesh knows which share this order covers
        self.postMessage({ indices, key: e.data.key, density: e.data.density ?? 1 }, [indices.buffer])
      }
    }
  }
}

// ─── Streaming sort worker (Phase 1+2, dev-gated) ────────────────────────────
// Compacted sort. The GPU splat atlas ("pool") is a fixed `capacity`-slot
// slab that SplatOctree pages leaf-node chunks in and out of; most slots are
// dead at any moment. Rather than hold a slot-indexed array and re-scan the
// whole slab every sort (O(capacity) + a fresh output alloc per frame — the
// GC-churn / sort-lag that showed up as stutter and depth-order lag while
// navigating), this keeps only the LIVE splats in a dense array and sorts
// O(live). This mirrors how PlayCanvas's own WebGL2 splat sorter works.
//
//   liveXYZI      dense (x,y,z,importance) per live splat, [0, liveCount*4)
//                 — exactly sog-chunk-worker.js's `sortXYZI` payload
//   liveSlot      dense: compact index k -> its GPU atlas slot index
//   slotToCompact sparse: atlas slot -> compact index k, or -1 if dead
//
// write/clear/move keep the dense arrays compact via swap-remove; the sort
// output is a list of atlas SLOT indices (what the vertex shader's
// splatIndex attribute needs), written into a recycled buffer the main
// thread hands back via 'recycle'.
function createStreamingWorker(self: any) {
  const NBUCKETS = 65536 // depth buckets — fine enough that near splats a few
                         // cm apart don't collide into one bucket (which
                         // makes their composite order flip frame-to-frame =
                         // fine detail like signs/marks flickering). Prefix
                         // sum over 64K is still ~0.1ms.
  const DEPTH_THRESHOLD = -0.0001
  let capacity = 0
  let liveXYZI: Float32Array = null!
  let liveSlot: Uint32Array = null!
  let slotToCompact: Int32Array = null!
  let liveCount = 0
  // Per-sort scratch, allocated once at init, reused every sort.
  let sDepth: Float32Array = null!
  let sDepthBits: Int32Array = null! // sDepth's bytes read as int32 (same buffer)
  let sValidK: Uint32Array = null!
  // Depth buckets spaced by distance (the float's own bits), not evenly
  // across [nearest, farthest]: even spacing over a scene with far floaters
  // (Cinevilla Forest Village spans ~11 km) made each bucket up to ~17 cm
  // deep, so nearby splats closer than that shared a bucket and their order
  // flipped as the camera moved — flicker everywhere while moving, steady at
  // rest. Float bits grow ~logarithmically with distance: ~1 mm buckets at
  // 5 m, ~1 cm at 50 m. Set from ?sortKey=linear (old behaviour) via 'init'.
  let linearKey = false
  let sBucket: Uint16Array = null!
  const counts = new Uint32Array(NBUCKETS)
  const starts = new Uint32Array(NBUCKETS)
  // Rotating output buffers: one is in flight to the main thread while the
  // next sort fills another; the main thread returns each via 'recycle'.
  const freeOut: Uint32Array[] = []

  function allocAll(cap: number) {
    capacity = cap
    liveXYZI = new Float32Array(cap * 4)
    liveSlot = new Uint32Array(cap)
    slotToCompact = new Int32Array(cap).fill(-1)
    sDepth = new Float32Array(cap)
    sDepthBits = new Int32Array(sDepth.buffer)
    sValidK = new Uint32Array(cap)
    sBucket = new Uint16Array(cap)
    liveCount = 0
  }

  // Swap-remove every live splat in atlas slots [offset, offset+count).
  function clearRange(offset: number, count: number) {
    const end = offset + count
    for (let slot = offset; slot < end; slot++) {
      const k = slotToCompact[slot]
      if (k < 0) continue
      const last = liveCount - 1
      if (k !== last) {
        liveXYZI[k * 4] = liveXYZI[last * 4]
        liveXYZI[k * 4 + 1] = liveXYZI[last * 4 + 1]
        liveXYZI[k * 4 + 2] = liveXYZI[last * 4 + 2]
        liveXYZI[k * 4 + 3] = liveXYZI[last * 4 + 3]
        const movedSlot = liveSlot[last]
        liveSlot[k] = movedSlot
        slotToCompact[movedSlot] = k
      }
      slotToCompact[slot] = -1
      liveCount = last
    }
  }

  // Append `count` splats occupying atlas slots [offset, offset+count).
  // clearRange first is normally a no-op (the pool quarantines freed slots
  // long enough for their 'clear' to land) but bulletproofs against a slot
  // being reused while still marked live — otherwise the old compact entry
  // orphans into a ghost splat.
  function writeRange(offset: number, count: number, data: Float32Array) {
    clearRange(offset, count)
    let k = liveCount
    liveXYZI.set(data, k * 4)
    for (let i = 0; i < count; i++) {
      const slot = offset + i
      liveSlot[k] = slot
      slotToCompact[slot] = k
      k++
    }
    liveCount = k
  }

  // Off-screen cull margin, in clip space: the vertex shader drops a splat
  // whose centre is beyond 1.2 × w; the sort drops it beyond margin × w — a
  // wider band, so a sort that lands after the camera turned still has the
  // splats now coming into view. update() sends a per-axis margin sized to
  // how fast the camera turns (Infinity = no cull on that axis); this is the
  // fallback for a message without one.
  const FRUSTUM_MARGIN = 1.6

  function sortSplats(view: Float32Array, hashed: boolean, fr: Float32Array | null, minPxDist: number, marginX = FRUSTUM_MARGIN, marginY = FRUSTUM_MARGIN): { buf: Uint32Array; count: number } {
    const v0 = view[0], v1 = view[1], v2 = view[2], v3 = view[3]
    // fr: clip-space x, y, w rows of model-view-projection (12 floats, in
    // the sort data's coordinates). Without it (older callers) no
    // off-screen cull.
    const useFr = !hashed && !!fr
    const fx0 = useFr ? fr![0] : 0, fx1 = useFr ? fr![1] : 0, fx2 = useFr ? fr![2] : 0, fx3 = useFr ? fr![3] : 0
    const fy0 = useFr ? fr![4] : 0, fy1 = useFr ? fr![5] : 0, fy2 = useFr ? fr![6] : 0, fy3 = useFr ? fr![7] : 0
    const fw0 = useFr ? fr![8] : 0, fw1 = useFr ? fr![9] : 0, fw2 = useFr ? fr![10] : 0, fw3 = useFr ? fr![11] : 0
    let minD = Infinity, maxD = -Infinity
    let valid = 0
    for (let k = 0; k < liveCount; k++) {
      const b = k * 4
      const x = liveXYZI[b], y = liveXYZI[b + 1], z = liveXYZI[b + 2]
      const d = v0 * x + v1 * y + v2 * z + v3
      // Cull splats behind the camera (the vertex shader would reject them
      // anyway) unless alpha-hash blending wants every splat.
      if (!hashed && !(d < 0 && liveXYZI[b + 3] > DEPTH_THRESHOLD * d)) continue
      // …and sub-pixel ones: on-screen size × opacity (the 4th value is
      // max scale × opacity) below SUBPIXEL_PX — minPxDist = SUBPIXEL_PX /
      // focal, so the test is size·opacity / distance < minPxDist. Thousands
      // of these per far-away area cost full per-splat GPU work for a
      // fraction of a pixel each.
      if (minPxDist > 0 && !hashed && liveXYZI[b + 3] < -d * minPxDist) continue
      // …and off-screen ones: each splat handed to the GPU costs vertex work
      // and (on Apple's tile-based GPUs) binning even when the shader then
      // drops it — the bulk of the frame on an iPad.
      if (useFr) {
        const w = fw0 * x + fw1 * y + fw2 * z + fw3
        const limX = marginX * w
        const cx = fx0 * x + fx1 * y + fx2 * z + fx3
        if (cx < -limX || cx > limX) continue
        const limY = marginY * w
        const cy = fy0 * x + fy1 * y + fy2 * z + fy3
        if (cy < -limY || cy > limY) continue
      }
      // Distance in front of the camera (d < 0 there); clamped to 5 cm so
      // one splat touching the lens can't stretch the bucket range.
      const dist = -d
      sDepth[valid] = dist < 0.05 && !hashed ? 0.05 : dist
      sValidK[valid] = k
      valid++
      if (dist < minD) minD = dist
      if (dist > maxD) maxD = dist
    }
    const out = freeOut.pop() || new Uint32Array(capacity)
    if (valid === 0) return { buf: out, count: 0 }
    counts.fill(0)
    // Bucket 0 = farthest (drawn first, back to front).
    if (!hashed && !linearKey) {
      // Every distance here is >= 0.05, so its int32 bits order like it.
      let minB = 0x7fffffff, maxB = 0
      for (let i = 0; i < valid; i++) {
        const b = sDepthBits[i]
        if (b < minB) minB = b
        if (b > maxB) maxB = b
      }
      const inv = (NBUCKETS - 1) / ((maxB - minB) || 1)
      for (let i = 0; i < valid; i++) {
        const bk = ((maxB - sDepthBits[i]) * inv) | 0
        sBucket[i] = bk
        counts[bk]++
      }
    } else {
      // Even spacing (and alpha-hash mode, which keeps splats behind the
      // camera — negative distances).
      const inv = (NBUCKETS - 1) / ((maxD - minD) || 1)
      for (let i = 0; i < valid; i++) {
        const bk = ((maxD - sDepth[i]) * inv) | 0
        sBucket[i] = bk
        counts[bk]++
      }
    }
    starts[0] = 0
    for (let b = 1; b < NBUCKETS; b++) starts[b] = starts[b - 1] + counts[b - 1]
    for (let i = 0; i < valid; i++) out[starts[sBucket[i]]++] = liveSlot[sValidK[i]]
    return { buf: out, count: valid }
  }

  self.onmessage = (e: { data: any }) => {
    const d = e.data
    switch (d.method) {
      case 'init':
        allocAll(d.capacity)
        linearKey = !!d.linearKey
        break
      case 'write':
        writeRange(d.offset, d.count, new Float32Array(d.data))
        break
      case 'clear':
        clearRange(d.offset, d.count)
        break
      case 'move':
        // Atomic LOD swap: new data at its new slot + old slot cleared in
        // one message, so no sort lands mid-swap and draws the chunk twice.
        writeRange(d.offset, d.count, new Float32Array(d.data))
        if (d.oldOffset >= 0 && d.oldOffset !== d.offset) clearRange(d.oldOffset, d.oldCount)
        break
      case 'recycle':
        freeOut.push(new Uint32Array(d.buffer))
        break
      case 'sort': {
        if (!liveXYZI) break
        const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now())
        const { buf, count } = sortSplats(new Float32Array(d.view), !!d.hashed, d.frustum ? new Float32Array(d.frustum) : null, d.minPxDist || 0,
          d.marginX ?? FRUSTUM_MARGIN, d.marginY ?? FRUSTUM_MARGIN)
        const sortMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0
        // @ts-ignore
        self.postMessage({ indices: buf, count, key: d.key, sortMs, liveCount }, [buf.buffer])
        break
      }
    }
  }
}

export const isMobileDevice = /iPhone|iPod|Android/i.test(
  typeof navigator !== 'undefined' ? navigator.userAgent : ''
)
// iPad Pro 2025 runs out of memory with SH textures (12× the base texture size);
// treat iPad the same as iPhone for memory-intensive decisions.
//
// iPadOS Safari masquerades as desktop Safari (platform 'MacIntel',
// maxTouchPoints 5), so that pair is the usual "is this really an iPad" test —
// but some Apple-Silicon Macs also report maxTouchPoints > 1 (Safari/Sidecar
// quirk), which was wrongly dragging real MacBooks onto the low-memory path
// (forced .splat over .sog, SH degree 1, 50% splat density — a visibly worse
// render). A real Mac's PRIMARY pointer is the
// trackpad (fine + hover); an iPad's primary pointer is always touch, even
// with a Magic Keyboard trackpad attached — so require that the primary
// pointer is NOT a fine/hover one before trusting the MacIntel+touch signal.
const primaryPointerIsFine = typeof matchMedia === 'function'
  && matchMedia('(hover: hover) and (pointer: fine)').matches
const isIPadDevice = typeof navigator !== 'undefined' && (
  /iPad/i.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1 && !primaryPointerIsFine)
)
export const isLowMemoryDevice = isMobileDevice || isIPadDevice
// Phones only — NOT tablets: iPhone/iPod, or Android with "Mobile" in the
// UA (Android tablets omit it; isMobileDevice above counts them as phones).
// iPads never match (iPadOS reports as desktop Safari, or "iPad").
export const isPhoneDevice = typeof navigator !== 'undefined'
  && (/iPhone|iPod/i.test(navigator.userAgent) || (/Android/i.test(navigator.userAgent) && /Mobile/i.test(navigator.userAgent)))

// WebGPU splat renderer choice. Default: our own SplatMaterial ported to TSL
// (three/splatNodeMaterial.js) with a GPU sort (three/splatGpuSort.js) and
// sRGB-space blending — pixel-identical to WebGL in headless comparison.
// ?webgpuAddon=1: three's GaussianSplat addon instead (kept for comparison;
// visibly softer/hazier — coarse 4096-bin sort, fixed blur kernel).
export const useWebGPUAddon = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('webgpuAddon') === '1'

// ─── Renderer choice (Quality menu → Renderer: Auto / WebGPU / WebGL) ────────
// ?renderer=webgpu|webgl is a manual pick and always wins, on every device
// (Safari included — useful for re-testing future Safari releases). Legacy
// ?webgpu=1 / ?webgpu=force map to ?renderer=webgpu. Shared by
// SceneHandler.js (which renderer to construct) and QualityModal.js (what
// "Auto" shows) so the two can never disagree.
//
// WebKit (Safari, and every iOS browser) is never an Auto WebGPU target:
// measured 2026-10-01 on an M3 Pro, Safari's WebGPU saturated the GPU badly
// enough to lag the whole machine, while its WebGL beat Chrome's WebGL.
// Chrome's WebGPU beats both.
//
// WEBGPU_AUTO_READY: on since 2026-10-16 — the WebGPU path matches WebGL
// (our TSL SplatMaterial port: SH, covClamp, live splat share / SH degree,
// GPU sort, sRGB blending), so Auto picks WebGPU for static models in
// desktop Chrome / Edge / Firefox. Safari, every iOS browser, phones and
// Android tablets stay WebGL;
// Settings → Renderer (?renderer=webgl) still forces WebGL.
// Off since 2026-10-25: measured on an M3 Pro (stream test page, Concert Hall
// V1, ~6M splats, app resolution), WebGL drew the frame in 42-45 ms, WebGPU
// in 73-110 ms + 7-11 ms sort — WebGL is the faster one in desktop Chrome
// now. Turn back on once WebGPU measures faster.
const WEBGPU_AUTO_READY = false
export const hasWebGPUSupport = typeof navigator !== 'undefined' && !!(navigator as any).gpu
export const isWebKitBrowser = typeof navigator !== 'undefined'
  && /AppleWebKit/.test(navigator.userAgent)
  && !/Chrome\/|Chromium\/|Edg\//.test(navigator.userAgent)
export const autoRenderer: 'webgpu' | 'webgl' =
  WEBGPU_AUTO_READY && hasWebGPUSupport && !isWebKitBrowser && !isMobileDevice ? 'webgpu' : 'webgl'

// Streamed-SOG models have their own Auto: WebGPU on desktop in Chrome or
// Firefox when the browser actually exposes navigator.gpu; WebGL everywhere
// else: phones and tablets (on iPad every browser is WebKit, whose WebGPU
// measured far slower than its WebGL here), Safari, and other browsers
// (Edge, Opera, Samsung Internet…).
// Static models keep autoRenderer above. A manual ?renderer= pick still wins.
const _ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
export const isChromeBrowser = (/Chrome\//.test(_ua) && !/Edg\/|OPR\/|SamsungBrowser\/|YaBrowser\/|Vivaldi\//.test(_ua))
  || /CriOS\//.test(_ua)
export const isFirefoxBrowser = /Firefox\/|FxiOS\//.test(_ua)
// WEBGPU_STREAMED_AUTO: off since 2026-10-25 — measured on an M3 Pro (stream
// test page, Cinevilla SS, app resolution), WebGL was faster in desktop
// Chrome too: heavy view 19.3 ms vs WebGPU 27.2 + 7.0 ms sort, wall 12.2 vs
// 13.7 + 2.1 ms (Safari: 20.0 vs 18.9 + 2.7, 13.7 vs 14.0 + 2.6). Turn back on
// once WebGPU measures faster. Phones and tablets stay WebGL either way.
const WEBGPU_STREAMED_AUTO = false
export const autoRendererStreamed: 'webgpu' | 'webgl' =
  WEBGPU_STREAMED_AUTO && hasWebGPUSupport && !isLowMemoryDevice && (isChromeBrowser || isFirefoxBrowser) ? 'webgpu' : 'webgl'

export function requestedRenderer(params: URLSearchParams): '' | 'webgpu' | 'webgl' {
  const r = params.get('renderer')
  if (r === 'webgpu' || r === 'webgl') return r
  const legacy = params.get('webgpu')
  return legacy === '1' || legacy === 'force' ? 'webgpu' : ''
}

// Rough multiplier from raw .sog file size to estimated peak WASM decode
// memory, hand-tuned from observed crashes (see the SOG crash-recovery
// screen in Scene.js, and resolveSplatUrl's pre-download memory check in
// SceneHandler.js) rather than derived from measured stats.
export const SOG_DECODE_MEMORY_MULTIPLIER = 16

// Adaptive default for phones (iPhone/Android). iPad and desktop always render
// the full model at High. A phone scales BOTH the splat count and the render
// preset to the .splat's size so a large model loads instead of crash-looping
// (a full 6M .splat is ~1GB — an iPhone 13 mini can't hold it). An explicit
// ?resolution= / ?splats= (Quality menu pick) always wins over this.
//   > 4M splats  →  50%  + Balanced
//   > 2.5M       →  75%  + High
//   otherwise    →  100% + High
// Splat-count decimation itself (any %, any device via the menu) is a uniform
// spatial subsample in lazyLoad — .splat records are spatially ordered, so
// truncation would leave holes.
//
// 2026-10-01: tried replacing this with a real splat-count budget (~1M
// mobile / ~3M desktop, matching PlayCanvas's own guidance) to address a
// slow/laggy WebGPU-on-Safari investigation. Reverted at the user's
// explicit instruction — defaulting every desktop load to half detail (or
// less) just to work around one renderer/browser's current performance
// ceiling was the wrong trade for a capable desktop machine; "fewer
// splats" was correctly pushed back on as hiding the problem, not fixing
// it. Static-path optimization work continues, but not via this lever.
// Auto navigation quality (resolution while the camera moves — Settings →
// Quality → Navigation quality, SceneHandler's lower-resolution-while-moving)
// from the number of splats actually drawn: small models stay full
// resolution, big ones trade resolution for smoothness. A manual pick
// (?navQuality=) always wins. Shared by SceneHandler + QualityModal.
export function autoNavQuality(_splatCount?: number | null): number {
  // Full resolution while moving, every device. Adaptive quality may lower
  // it (to 70% at most) as its last step — see SceneHandler's navLever.
  return 1
}

export function autoTierFor(splatCount: number): { quality: string; splats: number } {
  if (isMobileDevice) {
    if (splatCount > 4_000_000) return { quality: 'balanced', splats: 50 }
    if (splatCount > 2_500_000) return { quality: 'high', splats: 75 }
  }
  return { quality: 'high', splats: 100 }
}

// Trim numVertices to the requested splat-count % (records srcVertexCount for
// the subsamplers). Applies to every format — splat via lazyLoad's Bresenham
// stream filter, SOG/PLY via an output→source index map in their push fns.
function applySplatDensity(shared: SharedState) {
  shared.srcVertexCount = shared.numVertices
  const pct = shared.splatDensity
  if (!pct || pct >= 100 || shared.numVertices <= 1) return
  shared.numVertices = Math.floor(shared.numVertices * pct / 100)
  console.warn(`[Splat] ${pct}% splat count: ${shared.srcVertexCount.toLocaleString()} → ${shared.numVertices.toLocaleString()} (raise it in the Quality menu for the full model)`)
}

// Output index o (0..numVertices-1) → source index, for the SOG/PLY push
// functions. Uniform: every (src/out)-th source splat. Identity at 100%.
function srcIndexMapper(shared: SharedState): (o: number) => number {
  const src = shared.srcVertexCount ?? shared.numVertices
  if (!shared.splatDensity || shared.splatDensity >= 100 || src <= shared.numVertices) return (o) => o
  const step = src / shared.numVertices
  return (o) => Math.min((o * step) | 0, src - 1)
}

// navigator.deviceMemory (Chrome/Android only) reports a coarse device-class
// bucket in GB, not exact available memory - treat it the same way: a class
// signal, not a byte-accurate budget. Falls back to the UA-based low-memory
// heuristic above where the API doesn't exist (Safari/iOS, Firefox, desktop).
//
// The low-memory budget is NOT "how much RAM this device has" - iOS Safari
// enforces its own much stricter per-tab memory ceiling at the WebKit/OS
// level, largely independent of total device RAM. Confirmed empirically: a
// 47.7MB .sog (~728MB estimated decode via SOG_DECODE_MEMORY_MULTIPLIER)
// reliably crashed an iPhone 13 mini, yet fell well under a 2GB-assumed
// budget at the 50% safety margin used in resolveSplatUrl - that budget was
// tuned to "device RAM," not the actual browser-tab ceiling, so it never
// triggered the fallback. 1GB reflects the tab ceiling instead.
export function estimateAvailableMemoryBytes(): number {
  const deviceMemoryGB = typeof navigator !== 'undefined' ? (navigator as any).deviceMemory : undefined
  if (typeof deviceMemoryGB === 'number') return deviceMemoryGB * 1024 * 1024 * 1024
  return (isLowMemoryDevice ? 1 : 8) * 1024 * 1024 * 1024
}

export class SplatLoader extends THREE.Loader {
  gl: THREE.WebGLRenderer
  chunkSize: number
  sh: boolean
  shDegree: number
  // Splat-count percentage (100 | 75 | 50). Set by SceneHandler once it knows
  // the model size (the ?splats= menu pick, or the size-based phone tier);
  // undefined until then, treated as 100.
  splatDensity?: number
  // The pre-reduction splat count — set alongside numVertices whenever the
  // splat count is trimmed, so the SOG/PLY subsamplers can map output index
  // back to a source index.
  srcVertexCount?: number
  // See SharedState.onStageProgress — set by SceneHandler before load().
  onStageProgress?: (stage: 'decode' | 'push' | 'build', fraction: number) => void

  // shDegreeOverride: explicit SH degree (0-3) from ?sh= — 0 disables SH
  // entirely, 1-3 enables it at that exact degree. Undefined (no ?sh= param)
  // keeps the previous auto behavior: SH on except on phones, capped to
  // degree 1 on phones/iPad (4× less memory than degree 3).
  constructor(gl: THREE.WebGLRenderer, chunkSize = 250000, shDegreeOverride?: number, splatDensity?: number) {
    super()
    this.gl = gl
    this.chunkSize = chunkSize
    // undefined until SceneHandler resolves the tier from the model size
    this.splatDensity = splatDensity as any
    if (shDegreeOverride !== undefined) {
      this.sh = shDegreeOverride > 0
      this.shDegree = shDegreeOverride
    } else {
      this.sh = !isMobileDevice
      this.shDegree = isLowMemoryDevice ? 1 : 3
    }
  }

  async loadAsync(url: string, onProgress?: (event: ProgressEvent) => void, onError?: (event: ErrorEvent) => void, etag?: string) {
    return new Promise((res) => this.load(url, res, onProgress, onError, etag))
  }

  // ─── Streamed SOG (Phase 1+2, dev-gated) ───────────────────────────────────
  // Separate entry point from load()/loadAsync() above — nothing here is
  // reachable from, or touches the state of, the existing .sog/.splat/.ply
  // path. Returns a SharedState-shaped object (typed `any`: it doesn't
  // populate the one-shot-load-only fields like plyBuffer/sogDataTable) built
  // from a fixed-budget SplatPool + SplatOctree instead of one-shot GPU
  // upload, so `new Splat(shared, camera, ...)` (Splat.ts's THREE.Mesh
  // subclass, unchanged) works against it exactly as it does today.
  async loadStreamed(lodMetaUrl: string, budget = 4_000_000, opts: { lodDist?: number; lodMult?: number; prewarm?: boolean; knapsack?: boolean; exactPack?: boolean; defrag?: boolean; poolCapacity?: number; accessQuery?: string } = {}): Promise<any> {
    // Resolve against the page URL first: manager.resolveURL() only applies
    // registered URL modifiers and can hand back a bare path (e.g.
    // "/test-fixtures/.../lod-meta.json"), which isn't a valid base for the
    // WHATWG URL() resolution parseLodMeta does for every filename/child URL.
    const absoluteLodMetaUrl = new URL(this.manager.resolveURL(lodMetaUrl), window.location.href).toString()
    // Download pass (CloudFront signed query) for every request of this tree;
    // see lodChunkCache.js's setAccessQuery.
    setAccessQuery(opts.accessQuery)
    const res = await fetch(accessUrl(absoluteLodMetaUrl))
    const json = await res.json()
    // ?mergeTiles=<splats>: merge small tiles (test; see parseLodMeta).
    const mergeTiles = parseInt(new URLSearchParams(window.location.search).get('mergeTiles') || '', 10) || 0
    const meta = parseLodMeta(json, absoluteLodMetaUrl, { mergeTiles })
    // Version tag for the local chunk cache (utils/lodChunkCache.js): the
    // tree's own ETag, so a regenerated tree is never served from cache.
    ;(meta as any).cacheTag = res.headers.get('ETag') || res.headers.get('Last-Modified') || ''
    // Size-bounded chunk cache: per-device byte cap, and which files count
    // as "coarse" (evicted last, preloaded) — see utils/lodChunkCache.js.
    // Capped at 1.5s so a slow storage estimate never delays the first view.
    const cacheTag = (meta as any).cacheTag as string
    const deviceClass = isPhoneDevice ? 'phone' : isLowMemoryDevice ? 'tablet' : 'desktop'
    const cacheCap: number = await Promise.race([
      chunkCacheCap(deviceClass),
      new Promise<number>((r) => setTimeout(() => r(deviceClass === 'desktop' ? 512 * 1024 * 1024 : 100 * 1024 * 1024), 1500)),
    ])
    const cachePlan = planPreload(meta, cacheCap, deviceClass)
    ;(meta as any).fileCoarse = cachePlan.fileCoarse
    const treeDir = absoluteLodMetaUrl.slice(0, absoluteLodMetaUrl.lastIndexOf('/') + 1)
    const cacheOpened = openTreeCache(treeDir, cacheTag).then(() => enforceCap(cacheCap))

    // SH re-enabled: it was forced off earlier this session to rule it out
    // while chasing the RangeError/lag/grey-block bugs (all since found and
    // fixed elsewhere — none were actually SH-related), and
    // SplatPool._writeShTexture's per-texel upload loop (the one genuine
    // SH-specific perf risk) is now batched. Without SH, real captured
    // scenes render flat/DC-color-only — no view-dependent specular/
    // shading detail — which is a plausible read on "quality never
    // improved no matter what LOD fix landed": every fix so far changed
    // *which* level renders, none of them touched shading richness.
    // ?exactPack=1 selects the exact-size allocator (Part B of the
    // "match PlayCanvas" rewrite — see STREAMING-SOG-STATUS.md and
    // /Users/uldis.liepins/.claude/plans/floating-launching-blum.md); the
    // legacy buddy-allocator SplatPool stays the default and fully intact
    // for instant rollback. Static (.splat/.ply/.sog one-shot) loads never
    // go through this function at all, so this switch has zero effect on
    // that path.
    const PoolClass = opts.exactPack ? SplatPoolExact : SplatPool
    // Neither the budget nor the pool needs to exceed the model itself (its
    // full-detail splat count: every leaf at its finest level) — a model
    // smaller than the device default just loads all of it.
    const modelSplats = meta.leaves.reduce((sum: number, leaf: any) => {
      const levels = Object.keys(leaf.lods || {}).map(Number)
      const finest = levels.length ? leaf.lods[Math.min(...levels)] : null
      return sum + ((finest && finest.count) || 0)
    }, 0)
    if (modelSplats > 0) budget = Math.min(budget, modelSplats)
    // Pool size can exceed the starting budget (opts.poolCapacity, the
    // device max — Infinity = the whole model) so the budget can be raised
    // live without a reload.
    let budgetMax = Math.max(budget, opts.poolCapacity || 0)
    if (modelSplats > 0) budgetMax = Math.min(budgetMax, modelSplats)
    // Model size unknown and no device max (desktop = Infinity): never size
    // the pool to infinity — stay at the starting budget.
    if (!Number.isFinite(budgetMax)) budgetMax = budget
    // The pool gets POOL_HEADROOM more room than the largest budget allowed:
    // swapping a chunk to a finer level briefly needs both versions, and a
    // pool sized exactly to the budget kept failing those swaps ("pool full,
    // keeping previous LOD"). The budget itself stays capped at budgetMax.
    const POOL_HEADROOM = 0.125
    const poolSize = Math.ceil(budgetMax * (1 + POOL_HEADROOM))
    const pool = new PoolClass(this.gl, poolSize, this.sh ? this.shDegree : 0)
    loadTrace.start() // ?loadTrace=1 — see utils/loadTrace.js
    const sortWorker = new Worker(
      URL.createObjectURL(new Blob(['(', createStreamingWorker.toString(), ')(self)'], { type: 'application/javascript' }))
    )
    // Size the worker's arrays to the pool's ACTUAL addressable space
    // (width*height, rounded up from `budget` to fill a whole last texture
    // row — see SplatPool's constructor). pool.alloc() can hand back any
    // slot up to pool.capacity; anything less risks an out-of-bounds write
    // for a slot landing in the rounding slack. The octree's own `budget`
    // (setBudget below) is a separate, smaller policy target.
    sortWorker.postMessage({ method: 'init', capacity: pool.capacity, linearKey: new URLSearchParams(window.location.search).get('sortKey') === 'linear' })

    // 2 decode workers, not 4: each now serialises its decodes (see
    // sog-chunk-worker), so worker count == max concurrent decodes == peak
    // decode memory. Large chunks (a -C 350 city fixture) are ~300 MB each
    // to decode; 4 at once blew the heap. Kept at 2 even on a low-memory
    // device — dropping it to 1 (tried live 2026-09-14) fixed the crash but
    // roughly halved fill throughput, and distant content then never caught
    // up even given minutes on a fast connection: a processing bottleneck,
    // not a network one. The crash fix didn't actually depend on worker
    // count — see MAX_WRITE_QUEUE_LOW_MEMORY / BOOST_REQUEST_CAP_LOW_MEMORY
    // in splatOctree.js, which close the actual gap (the decode-cache
    // eviction never touches a file backing a resident leaf or a queued
    // write, so THAT floor — not concurrency — was the real ceiling).
    // lowMemory also scales the octree's own decode cache — see
    // MAX_DECODE_CACHE_LOW_MEMORY in splatOctree.js.
    // Phones 2; iPads 3 like desktop (2026-10-28) — they have the memory,
    // and a third worker fills in detail faster after a move.
    const octree = new SplatOctree(meta, { pool, sortWorker, decodeWorkerCount: isMobileDevice ? 2 : 3, lodDist: opts.lodDist || 0, lodMult: opts.lodMult || 0, prewarm: opts.prewarm !== false, lowMemory: isLowMemoryDevice, knapsack: opts.knapsack === true, defrag: opts.defrag === true, visibleCap: streamDrawCap() })
    octree.budgetMax = budgetMax
    octree.setBudget(budget)


    const shared: any = {
      gl: this.gl,
      url: this.manager.resolveURL(lodMetaUrl),
      worker: sortWorker,
      manager: this.manager,
      // Skips connect()'s one-shot sogDataTable/plyBuffer/lazyLoad dispatch
      // branch entirely — see connect()'s own comment.
      loading: true,
      loaded: false,
      loadedVertexCount: 0,
      bufferTextureWidth: pool.width,
      bufferTextureHeight: pool.height,
      centerAndScaleTexture: pool.centerAndScaleTexture,
      covAndColorTexture: pool.covAndColorTexture,
      hasSH: pool.hasSH,
      shDegree: pool.shDegree, // the pool may lower it to fit the max texture size
      shTexture: pool.shTexture,
      octree,
      pool,
      bound: meta.bound,
      contentCenter: meta.contentCenter,
      contentRadius: meta.contentRadius,
    }
    shared.connect = (target: TargetMesh) => connect(shared, target)
    shared.update = (target: TargetMesh, camera: THREE.Camera, hashed: boolean) => update(camera, shared, target, hashed)

    // Best-effort mapping onto the existing loadedVertexCount/loaded fields
    // (read by connect()'s wait() gate and by SceneHandler's "genuinely
    // finished" watcher) — "loaded" becomes true once the first chunk is
    // resident rather than at some single well-defined end-of-load moment,
    // since streaming has no such moment. Wiring this into the production
    // loading-percent UI is out of scope for this dev-gated phase.
    const statsTimer = setInterval(() => {
      shared.loadedVertexCount = pool.residentCount
      if (pool.residentCount > 0) shared.loaded = true
    }, 200)
    // Background preload into the local cache, sized to the device (see
    // planPreload): the whole tree on desktop when it fits in half the cap,
    // otherwise only the coarse levels — plus, on desktop, the detail
    // around the camera, refreshed as the camera moves. Nothing ahead of
    // need on Save-Data / slow connections. Starts after a few seconds so it
    // never competes with the first view's own chunks. The cap is enforced
    // periodically (decode workers write visited chunks as they go).
    const preloadAbort = new AbortController()
    // The background preload only runs while the current view has nothing
    // left to stream (octree.streamingIdle, steady for PRELOAD_IDLE_MS) —
    // checked before each chunk, so it also pauses as soon as moving the
    // camera needs new chunks. Before, it started 4 s in regardless and
    // took half the browser's connections from the first view's chunks.
    const PRELOAD_IDLE_MS = 1500
    const whenFree = async () => {
      let idleSince = 0
      while (!preloadAbort.signal.aborted) {
        const now = performance.now()
        if ((octree as any).streamingIdle) {
          if (!idleSince) idleSince = now
          if (now - idleSince >= PRELOAD_IDLE_MS) return
        } else {
          idleSince = 0
        }
        await new Promise((r) => setTimeout(r, 250))
      }
    }
    const preloadOpts = { fileCoarse: cachePlan.fileCoarse, signal: preloadAbort.signal, whenFree }
    let nearTimer: ReturnType<typeof setInterval> | undefined
    const capTimer = setInterval(() => { enforceCap(cacheCap) }, 15000)
    if (cacheTag && networkAllowsPrefetch()) {
      setTimeout(async () => {
        await cacheOpened
        if (preloadAbort.signal.aborted) return
        await preloadFiles(meta.filenames, cachePlan.preload, cacheTag, { ...preloadOpts, concurrency: deviceClass === 'desktop' ? 3 : 2 })
        await enforceCap(cacheCap)
        if (!cachePlan.nearBudget || preloadAbort.signal.aborted) return
        // Too big to keep whole: prefetch the finer files nearest the camera
        // (fine-detail entries are evicted first, so this window follows the
        // camera instead of piling up).
        const lastPos = new THREE.Vector3(NaN, NaN, NaN)
        const nearMoveEps = meta.contentRadius * 0.1
        let running = false
        const nearPass = async () => {
          const cam = (octree as any)._lastCamPos as THREE.Vector3
          if (running || preloadAbort.signal.aborted || !cam || Number.isNaN(cam.x)) return
          if (!Number.isNaN(lastPos.x) && cam.distanceTo(lastPos) < nearMoveEps) return
          running = true
          lastPos.copy(cam)
          const files = nearCameraFiles(meta, cachePlan, cam, Math.min(cachePlan.nearBudget, 300 * 1024 * 1024))
          await preloadFiles(meta.filenames, files, cacheTag, { ...preloadOpts, concurrency: 2 })
          await enforceCap(cacheCap)
          running = false
        }
        nearPass()
        nearTimer = setInterval(nearPass, 15000)
      }, 4000)
    }
    shared.destroy = () => { preloadAbort.abort(); clearInterval(statsTimer); clearInterval(capTimer); clearInterval(nearTimer); octree.destroy(); pool.destroy() }

    return shared
  }

  load(
    url: string,
    onLoad: (data: SharedState) => void,
    onProgress?: (event: ProgressEvent) => void,
    onError?: (event: ErrorEvent) => void,
    etag?: string
  ) {
    const urlPath = url.split('?')[0].toLowerCase()
    const isPLY = urlPath.endsWith('.ply') || urlPath.endsWith('.cply')
    const isSOG = urlPath.endsWith('.sog')
    const sh = this.sh
    const shared: SharedState = {
      gl: this.gl,
      url: this.manager.resolveURL(url),
      etag,
      worker: new Worker(
        URL.createObjectURL(
          new Blob(['(', createWorker.toString(), ')(self)'], { type: 'application/javascript' })
        )
      ),
      manager: this.manager,
      update: (target, camera, hashed) => update(camera, shared, target, hashed),
      connect: (target) => connect(shared, target),
      loading: false,
      loaded: false,
      loadedVertexCount: 0,
      chunkSize: this.chunkSize,
      totalDownloadBytes: 0,
      numVertices: 0,
      rowLength: (isPLY || isSOG) ? 0 : 3 * 4 + 3 * 4 + 4 + 4,
      maxVertexes: 0,
      bufferTextureWidth: 0,
      bufferTextureHeight: 0,
      stream: null!,
      centerAndScaleData: null!,
      covAndColorData: null!,
      covAndColorTexture: null!,
      centerAndScaleTexture: null!,
      sh,
      splatDensity: this.splatDensity,
      hasSH: isPLY && sh,
      shDegree: this.shDegree,
      shData: null,
      shTexture: null,
      plyBuffer: null,
      plyMeta: null,
      sogDataTable: null,
      onProgress,
      onStageProgress: this.onStageProgress
    }
    load(shared)
      .then(onLoad)
      .catch((e) => { onError?.(e); shared.manager.itemError(shared.url) })
  }
}

// ─── PLY header parsing ───────────────────────────────────────────────────────

function getPLYTypeSize(type: string): number {
  switch (type) {
    case 'float': case 'int': case 'uint': return 4
    case 'double': return 8
    case 'uchar': case 'char': return 1
    case 'ushort': case 'short': return 2
    default: return 4
  }
}

function parsePLYHeader(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer)
  const headerStr = new TextDecoder().decode(bytes.subarray(0, Math.min(32768, bytes.length)))
  const endToken = 'end_header\n'
  const headerEnd = headerStr.indexOf(endToken)
  if (headerEnd === -1) throw new Error('Invalid PLY: end_header not found')
  const headerLength = headerEnd + endToken.length
  const lines = headerStr.substring(0, headerEnd).split('\n')
  let vertexCount = 0
  const propOffsets: Record<string, number> = {}
  const propTypes: Record<string, string> = {}
  let fRestScale = 1.0
  let byteOffset = 0
  let inVertex = false
  for (const line of lines) {
    const parts = line.trim().split(/\s+/)
    if (parts[0] === 'comment' && parts[1] === 'f_rest_scale') {
      fRestScale = parseFloat(parts[2])
    } else if (parts[0] === 'element') {
      inVertex = parts[1] === 'vertex'
      if (inVertex) vertexCount = parseInt(parts[2])
    } else if (parts[0] === 'property' && inVertex) {
      propOffsets[parts[2]] = byteOffset
      propTypes[parts[2]] = parts[1]
      byteOffset += getPLYTypeSize(parts[1])
    }
  }
  return { vertexCount, headerLength, rowLength: byteOffset, propOffsets, propTypes, fRestScale }
}

// ─── Texture setup ────────────────────────────────────────────────────────────

// SH texture rows per gaussian: 12 for degree-3 (4 rows/channel × 3 channels,
// 16 coefficients/channel incl. DC), 9 for degree-2 (3 rows/channel, covers
// DC + degree1(3) + degree2(5) = 9 of the 12 slots, last row's y/z/w unused),
// 3 for degree-1 (1 row/channel, DC + degree1(3) = 4 slots).
const SH_ROWS_PER_GAUSSIAN = 12

function shRowsForDegree(shDegree: number) {
  if (shDegree === 1) return 3
  if (shDegree === 2) return 9
  return SH_ROWS_PER_GAUSSIAN
}

// Largest single SH texture we'll ask the GPU for. Above this, setupSharedTextures
// steps the SH degree down (3 -> 2 -> 1) rather than let texStorage2D fail —
// ANGLE/Metal throws GL_INVALID_OPERATION "allocation size too large" and the
// splat renders as a grey block. SH is stored fp16 (RGBA16F) = 8 bytes/texel,
// so at 16384px wide this 1 GiB cap keeps degree-3 through ~11M splats, degree-2
// through ~15M, degree-1 through ~44M. Lower it if grey blocks still appear on
// weaker GPUs; raise it if a capable GPU is being needlessly downgraded.
const MAX_SH_TEXTURE_BYTES = 1024 * 1024 * 1024

// float32 -> float16 bits, truncating. SH coefficients sit well inside fp16's
// normal range, so the underflow->0 / overflow->inf branches never fire for real
// data; skipping round-to-nearest keeps this cheap in the ~300M-call hot loop in
// pushSOGData / pushPLYData.
const _f32 = new Float32Array(1)
const _i32 = new Int32Array(_f32.buffer)
function toHalf(v: number): number {
  _f32[0] = v
  const x = _i32[0]
  const sign = (x >> 16) & 0x8000
  const e = ((x >> 23) & 0xff) - 112
  if (e <= 0) return sign
  if (e >= 31) return sign | 0x7c00
  return sign | (e << 10) | ((x & 0x7fffff) >> 13)
}

// Highest SH degree (<= want) whose fp16 texture fits both MAX_SH_TEXTURE_BYTES
// and the hard GL MAX_TEXTURE_SIZE height limit for this splat count. 0 = none fits.
function fittableShDegree(numVertices: number, maxTextureSize: number, want: number): number {
  const rows = Math.floor((numVertices - 1) / maxTextureSize) + 1
  for (let d = want; d >= 1; d--) {
    const h = rows * shRowsForDegree(d)
    if (h <= maxTextureSize && maxTextureSize * h * 4 * 2 <= MAX_SH_TEXTURE_BYTES) return d
  }
  return 0
}

// The SOG container is a ZIP with meta.json stored uncompressed. Pull the splat
// count out via the central directory (SOG writers use streaming mode — flag
// bit 3 — so the local headers carry zero sizes) without unzipping the payload,
// so the SH work can be sized before the decode worker runs. Best-effort: any
// hiccup returns null and the caller keeps its default.
function peekSogCount(buf: Uint8Array): number | null {
  try {
    const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength)
    let eocd = -1
    for (let i = buf.length - 22; i >= Math.max(0, buf.length - 65557); i--) {
      if (buf[i] === 0x50 && buf[i + 1] === 0x4b && buf[i + 2] === 0x05 && buf[i + 3] === 0x06) { eocd = i; break }
    }
    if (eocd < 0) return null
    let cd = dv.getUint32(eocd + 16, true)
    const cdEnd = cd + dv.getUint32(eocd + 12, true)
    while (cd + 46 <= cdEnd && buf[cd] === 0x50 && buf[cd + 1] === 0x4b && buf[cd + 2] === 0x01 && buf[cd + 3] === 0x02) {
      const fnLen = dv.getUint16(cd + 28, true)
      const exLen = dv.getUint16(cd + 30, true)
      const cmLen = dv.getUint16(cd + 32, true)
      const name = new TextDecoder().decode(buf.subarray(cd + 46, cd + 46 + fnLen))
      if (name === 'meta.json') {
        const size = dv.getUint32(cd + 20, true) // compressed == uncompressed (stored)
        const lho = dv.getUint32(cd + 42, true)
        const start = lho + 30 + dv.getUint16(lho + 26, true) + dv.getUint16(lho + 28, true)
        const meta = JSON.parse(new TextDecoder().decode(buf.subarray(start, start + size)))
        return typeof meta.count === 'number' ? meta.count : null
      }
      cd += 46 + fnLen + exLen + cmLen
    }
  } catch { /* fall through */ }
  return null
}

// Drop the CPU-side staging arrays once they're copied into GPU textures. For
// one-shot loads (SOG, PLY) nothing reads them again, so this hands ~0.7-1.5 GB
// back to the browser right when the sort worker and GPU driver need headroom.
// Also nulls each DataTexture's own reference so three can't re-upload a null
// buffer. The streamed .splat path (pushDataBuffer) reuses these across chunks
// and must never call this.
function freeStagingArrays(shared: SharedState) {
  const drop = (t: THREE.DataTexture | null | undefined) => {
    if (t && t.image) (t.image as any).data = null
  }
  drop(shared.shTexture)
  drop(shared.centerAndScaleTexture)
  drop(shared.covAndColorTexture)
  shared.shData = null
  shared.centerAndScaleData = null as any
  shared.covAndColorData = null as any
}

// WebGPU migration Stage 2 (?webgpu=1): WebGPURenderer has no
// getContext()/getParameter(MAX_TEXTURE_SIZE) (that's a WebGL2Rendering-
// Context API). Reads the real WebGPU device limit when reachable;
// otherwise falls back to 8192, the spec-guaranteed minimum for
// maxTextureDimension2D (every WebGPU-capable device supports at least
// this — see https://www.w3.org/TR/webgpu/#limits).
function getMaxTextureSize(shared: SharedState): number {
  const gl = shared.gl as any
  if (gl.isWebGPURenderer) {
    return gl.backend?.device?.limits?.maxTextureDimension2D || 8192
  }
  const context = shared.gl.getContext()
  return context.getParameter(context.MAX_TEXTURE_SIZE)
}

function setupSharedTextures(shared: SharedState) {
  const maxTextureSize = getMaxTextureSize(shared)
  shared.maxVertexes = maxTextureSize * maxTextureSize
  if (shared.numVertices > shared.maxVertexes) shared.numVertices = shared.maxVertexes
  shared.bufferTextureWidth = maxTextureSize
  shared.bufferTextureHeight = Math.floor((shared.numVertices - 1) / maxTextureSize) + 1

  shared.centerAndScaleData = new Float32Array(shared.bufferTextureWidth * shared.bufferTextureHeight * 4)
  shared.covAndColorData = new Uint32Array(shared.bufferTextureWidth * shared.bufferTextureHeight * 4)

  shared.centerAndScaleTexture = new THREE.DataTexture(
    shared.centerAndScaleData, shared.bufferTextureWidth, shared.bufferTextureHeight,
    THREE.RGBAFormat, THREE.FloatType
  )
  shared.centerAndScaleTexture.needsUpdate = true

  shared.covAndColorTexture = new THREE.DataTexture(
    shared.covAndColorData, shared.bufferTextureWidth, shared.bufferTextureHeight,
    THREE.RGBAIntegerFormat, THREE.UnsignedIntType
  )
  // WebGL-only: WebGPURenderer passes texture.internalFormat through as the
  // GPU format verbatim, and 'RGBA32UI' isn't a WebGPU format (it derives
  // 'rgba32uint' from RGBAIntegerFormat + UnsignedIntType by itself).
  if (!(shared.gl as any).isWebGPURenderer) shared.covAndColorTexture.internalFormat = 'RGBA32UI'
  shared.covAndColorTexture.needsUpdate = true

  if (shared.hasSH) {
    // SH is by far the largest buffer (3/9/12 texture rows per gaussian for
    // degree 1/2/3). Stored fp16 (RGBA16F, 8 bytes/texel): coefficients sit
    // within ~±4 where fp16's ~0.002 resolution is visually lossless, and it
    // halves both this staging array and the GPU texture vs fp32.
    const fitDegree = fittableShDegree(shared.numVertices, maxTextureSize, shared.shDegree)
    if (fitDegree === 0) {
      console.warn(
        `[SH] ${shared.numVertices.toLocaleString()} splats: no SH degree fits within ` +
        `${(MAX_SH_TEXTURE_BYTES / 1048576) | 0}MB / ${maxTextureSize}px — disabling SH for this model`
      )
      shared.hasSH = false
      shared.shData = null
      shared.shTexture = null
    } else {
      if (fitDegree !== shared.shDegree) {
        console.warn(
          `[SH] ${shared.numVertices.toLocaleString()} splats: SH degree ${shared.shDegree} ` +
          `texture too large — using degree ${fitDegree}`
        )
        shared.shDegree = fitDegree
      }
      const shHeight = shared.bufferTextureHeight * shRowsForDegree(shared.shDegree)
      shared.shData = new Uint16Array(shared.bufferTextureWidth * shHeight * 4)
      shared.shTexture = new THREE.DataTexture(
        shared.shData as any, shared.bufferTextureWidth, shHeight,
        THREE.RGBAFormat, THREE.HalfFloatType
      )
      shared.shTexture.needsUpdate = true
    }
  }

  publishDebugStats(shared, false)
}

function publishDebugStats(shared: SharedState, loaded: boolean) {
  const csBytes = shared.centerAndScaleData?.byteLength ?? 0
  const ccBytes = shared.covAndColorData?.byteLength ?? 0
  const shBytes = shared.shData?.byteLength ?? 0
  const totalCPU = csBytes + ccBytes + shBytes
  const mem = (typeof performance !== 'undefined' && (performance as any).memory)
    ? Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024)
    : null
  ;(window as any).__splatDebugStats = {
    numSplats: shared.numVertices,
    hasSH: shared.hasSH,
    shDegree: shared.shDegree,
    isLowMemory: isLowMemoryDevice,
    centerAndScaleMB: +(csBytes / 1024 / 1024).toFixed(1),
    covAndColorMB: +(ccBytes / 1024 / 1024).toFixed(1),
    shDataMB: +(shBytes / 1024 / 1024).toFixed(1),
    cpuTotalMB: +(totalCPU / 1024 / 1024).toFixed(1),
    gpuEstMB: +(totalCPU / 1024 / 1024).toFixed(1),
    heapMB: mem,
    loaded,
  }
}

// ─── Load entry point ─────────────────────────────────────────────────────────

async function load(shared: SharedState) {
  shared.manager.itemStart(shared.url)
  const response = await fetchWithModelCache(shared.url, shared.etag)
  if (!response.body) throw 'Failed to fetch file'

  const urlPath = shared.url.split('?')[0].toLowerCase()
  const isSOG = urlPath.endsWith('.sog')
  const isCPLY = urlPath.endsWith('.cply')
  const isPLY = urlPath.endsWith('.ply') || isCPLY

  if (isSOG) {
    const contentLength = parseInt(response.headers.get('Content-Length') ?? '0')
    const reader = response.body.getReader()
    const sogChunks: Uint8Array[] = []
    let sogReceived = 0, sogLastReported = 0
    ;(window as any).__splatDebugStats = { phase: 'downloading', downloadedMB: 0, totalMB: +(contentLength / 1024 / 1024).toFixed(1) }
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      sogChunks.push(value)
      sogReceived += value.length
      ;(window as any).__splatDebugStats.downloadedMB = +(sogReceived / 1024 / 1024).toFixed(1)
      if (shared.onProgress && contentLength > 0) {
        const pct = (sogReceived / contentLength) * 100
        if (pct - sogLastReported > 1) {
          shared.onProgress(new ProgressEvent('progress', { lengthComputable: true, loaded: sogReceived, total: contentLength }))
          sogLastReported = pct
        }
      }
    }
    // Always report the end of the download explicitly — the in-loop
    // reports are throttled to >1% steps and can miss the final bytes, and
    // SceneHandler's load-progress estimator uses "loaded >= total" as the
    // download→processing boundary.
    if (shared.onProgress && contentLength > 0) {
      shared.onProgress(new ProgressEvent('progress', { lengthComputable: true, loaded: contentLength, total: contentLength }))
    }
    const sogBuffer = new Uint8Array(sogReceived)
    let sogOff = 0
    for (const chunk of sogChunks) { sogBuffer.set(chunk, sogOff); sogOff += chunk.length }
    // Free the per-chunk buffers now that they're concatenated — the WASM
    // decode phase is expensive and we don't want both copies in RAM.
    sogChunks.length = 0
    ;(window as any).__splatDebugStats = { phase: 'WASM decoding', downloadedMB: +(sogReceived / 1024 / 1024).toFixed(1), totalMB: +(contentLength / 1024 / 1024).toFixed(1) }

    // Size the SH work before the decoder runs. If this splat count can't be
    // given a degree-3 SH texture anyway (fittableShDegree), tell the worker to
    // extract fewer bands — avoids building ~0.5-1 GB of transient Float32 SH
    // columns that would just be transferred and then thrown away.
    const sogMaxTex = getMaxTextureSize(shared)
    const sogCount = peekSogCount(sogBuffer)
    const workerShDegree = sogCount != null
      ? fittableShDegree(sogCount, sogMaxTex, shared.shDegree)
      : shared.shDegree
    const workerIncludeSH = shared.sh && workerShDegree > 0

    // Offload WASM parsing to a worker — keeps the heavy WASM heap out of the
    // main iOS tab, which would otherwise be killed by the OS memory limit.
    const { numRows, hasSH, cols } = await new Promise<{
      numRows: number; hasSH: boolean; cols: Record<string, Float32Array>
    }>((resolve, reject) => {
      const sogWorker = new Worker(new URL('./sog-reader-worker.js', import.meta.url))
      sogWorker.onmessage = (e: MessageEvent) => {
        if (e.data.type === 'progress') { shared.onStageProgress?.('decode', e.data.fraction); return }
        if (e.data.type === 'done') resolve(e.data)
        else reject(new Error(e.data.message))
        sogWorker.terminate()
      }
      sogWorker.onerror = (_e: ErrorEvent) => {
        reject(new Error(isLowMemoryDevice ? 'SOG_MOBILE_UNSUPPORTED' : 'SOG worker failed'))
        sogWorker.terminate()
      }
      // A tab-kill during this decode is caught by SceneHandler's
      // `sceneLoadGuard` (see there / Scene.js) — no SOG-specific marker.
      // isLowMemoryDevice (iPad included) → lodSelect:[0] in WASM, halving splat count
      sogWorker.postMessage({ sogBuffer: sogBuffer.buffer, includeSH: workerIncludeSH, mobile: isLowMemoryDevice, shDegree: workerShDegree || shared.shDegree }, [sogBuffer.buffer])
    })

    shared.sogDataTable = { cols, numRows }
    shared.hasSH = shared.sh && hasSH
    shared.numVertices = numRows
    applySplatDensity(shared)
    // Decimate the decoded columns in place (freeing each full one as we go)
    // so pushSOGData runs unchanged on the smaller set.
    if (shared.numVertices < numRows) {
      const map = srcIndexMapper(shared)
      for (const k of Object.keys(cols)) {
        const full = cols[k]
        const small = new Float32Array(shared.numVertices)
        for (let o = 0; o < small.length; o++) small[o] = full[map(o)]
        cols[k] = small
      }
      shared.sogDataTable.numRows = shared.numVertices
    }
    setupSharedTextures(shared)
    return shared
  }

  // PLY and CPLY always read the full file and parse the header regardless of SH.
  // Previously this only ran when hasSH=true, leaving mobile (sh=false) to fall
  // through to the splat streamer with rowLength=0, causing an immediate crash.
  if (isPLY) {
    const contentLength = parseInt(response.headers.get('Content-Length') ?? '0')
    shared.totalDownloadBytes = contentLength
    // Progress is reported in COMPRESSED (on-the-wire) bytes for .cply —
    // contentLength is the compressed size, so counting the decompressed
    // output against it overshot 100% early.
    let wireReceived = 0
    const wire = response.body.pipeThrough(new TransformStream<Uint8Array, Uint8Array>({
      transform(chunk, ctrl) { wireReceived += chunk.length; ctrl.enqueue(chunk) },
    }))
    const reader = isCPLY
      ? wire.pipeThrough(new DecompressionStream('gzip') as any).getReader() as ReadableStreamDefaultReader<Uint8Array>
      : wire.getReader()
    const chunks: Uint8Array[] = []
    let received = 0, lastReported = 0
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      chunks.push(value)
      received += value.length
      if (shared.onProgress && contentLength > 0) {
        const pct = (wireReceived / contentLength) * 100
        if (pct - lastReported > 1 && wireReceived < contentLength) {
          shared.onProgress(new ProgressEvent('progress', { lengthComputable: true, loaded: wireReceived, total: contentLength }))
          lastReported = pct
        }
      }
    }
    // Always report the end of the download explicitly — the in-loop
    // reports are throttled to >1% steps and can miss the final bytes, and
    // SceneHandler's load-progress estimator uses "loaded >= total" as the
    // download→processing boundary.
    if (shared.onProgress && contentLength > 0) {
      shared.onProgress(new ProgressEvent('progress', { lengthComputable: true, loaded: contentLength, total: contentLength }))
    }
    const buffer = new Uint8Array(received)
    let off = 0
    for (const chunk of chunks) { buffer.set(chunk, off); off += chunk.length }

    const meta = parsePLYHeader(buffer.buffer)
    shared.plyMeta = meta
    shared.plyBuffer = buffer.buffer
    shared.numVertices = meta.vertexCount
    applySplatDensity(shared)
    setupSharedTextures(shared)
  } else {
    const contentLength = response.headers.get('Content-Length')
    const totalDownloadBytes = contentLength ? parseInt(contentLength) : undefined
    if (totalDownloadBytes == undefined) throw 'Failed to get content length'
    shared.stream = response.body.getReader()
    shared.totalDownloadBytes = totalDownloadBytes
    shared.numVertices = Math.floor(shared.totalDownloadBytes / shared.rowLength)
    applySplatDensity(shared)
    setupSharedTextures(shared)
  }
  return shared
}

// ─── Splat lazy streaming (unchanged) ────────────────────────────────────────

async function lazyLoad(shared: SharedState) {
  shared.loading = true
  let bytesDownloaded = 0, bytesProcessed = 0
  const chunks: Array<Uint8Array> = []
  let lastReportedProgress = 0
  const lengthComputable = shared.totalDownloadBytes !== 0

  // Splat density < 100%: keep a uniform fraction of the splats. The whole
  // file still streams through — .splat records are spatially ordered, so the
  // only way to get a subsample that covers the whole model is to skip evenly
  // rather than stop early. `srcSplats` is the running global source index, so
  // the selection is continuous across chunk boundaries. Keep splat g iff
  // floor((g+1)·r) !== floor(g·r) — a Bresenham-style resample that keeps
  // exactly floor(N·r) of N (every 2nd at 50%, 3-of-4 at 75%).
  const keepRatio = shared.splatDensity && shared.splatDensity < 100 ? shared.splatDensity / 100 : 1
  let srcSplats = 0
  const rl = shared.rowLength
  const decimate = (src: Uint8Array, count: number): { buf: ArrayBufferLike; kept: number } => {
    if (keepRatio >= 1) { srcSplats += count; return { buf: src.buffer.slice(src.byteOffset, src.byteOffset + count * rl), kept: count } }
    const kept = Math.floor((srcSplats + count) * keepRatio) - Math.floor(srcSplats * keepRatio)
    const out = new Uint8Array(kept * rl)
    let w = 0
    for (let i = 0; i < count; i++) {
      const g = srcSplats + i
      if (Math.floor((g + 1) * keepRatio) !== Math.floor(g * keepRatio)) { out.set(src.subarray(i * rl, i * rl + rl), w * rl); w++ }
    }
    srcSplats += count
    return { buf: out.buffer, kept }
  }

  while (true) {
    try {
      const { value, done } = await shared.stream.read()
      if (done) break
      bytesDownloaded += value.length
      if (shared.totalDownloadBytes != undefined) {
        const percent = (bytesDownloaded / shared.totalDownloadBytes) * 100
        if (shared.onProgress && percent - lastReportedProgress > 1) {
          shared.onProgress(new ProgressEvent('progress', { lengthComputable, loaded: bytesDownloaded, total: shared.totalDownloadBytes }))
          lastReportedProgress = percent
        }
      }
      chunks.push(value)
      const bytesRemains = bytesDownloaded - bytesProcessed
      if (shared.totalDownloadBytes != undefined && bytesRemains > shared.rowLength * shared.chunkSize) {
        // Capped to chunkSize vertices per burst — NOT "however many bytes
        // happen to have accumulated". A fast connection (or, since caching
        // was added, an instant local-cache hit) can deliver several
        // chunkSize's worth of bytes between loop iterations; processing
        // all of it in one synchronous burst here (per-vertex quaternion/
        // matrix math for up to hundreds of thousands of vertices) blocked
        // the main thread long enough to visibly freeze the displayed
        // loading percentage for several seconds at a time, even though the
        // network transfer itself was continuing normally underneath the
        // whole time. Any leftover beyond one chunkSize just stays queued
        // in `chunks` and gets picked up on the next iteration.
        let vertexCount = Math.min(Math.floor(bytesRemains / shared.rowLength), shared.chunkSize)
        const concatenatedChunksbuffer = new Uint8Array(bytesRemains)
        let offset = 0
        for (const chunk of chunks) { concatenatedChunksbuffer.set(chunk, offset); offset += chunk.length }
        chunks.length = 0
        const consumedBytes = vertexCount * shared.rowLength
        if (bytesRemains > consumedBytes) {
          const extra_data = new Uint8Array(bytesRemains - consumedBytes)
          extra_data.set(concatenatedChunksbuffer.subarray(consumedBytes, bytesRemains), 0)
          chunks.push(extra_data)
        }
        const { buf, kept } = decimate(concatenatedChunksbuffer.subarray(0, consumedBytes), vertexCount)
        if (kept > 0) {
          const matrices = pushDataBuffer(shared, buf, kept)
          if (!shared.gpuSort) shared.worker.postMessage({ method: 'push', src: shared.url, length: shared.numVertices * 16, matrices: matrices.buffer }, [matrices.buffer])
        }
        bytesProcessed += consumedBytes
        // (No onProgress call here: the real, accurate download progress is
        // already reported above as bytes stream in. This used to also fire
        // a "progress" event claiming loaded === total - i.e. 100% - purely
        // because a chunk finished processing, well before the download was
        // actually complete. That created a second, conflicting progress
        // signal on the same channel, causing the displayed percentage to
        // spike toward 100% and then drop back down repeatedly.)
        // Yield back to the event loop — lets the browser actually paint
        // the progress update just reported above (a synchronous burst
        // immediately following it would otherwise defer that repaint
        // until this whole burst finishes) and keeps any single burst from
        // monopolizing the main thread regardless of how much data queued
        // up while it ran.
        await new Promise(r => setTimeout(r, 0))
      }
    } catch (error) { console.error(error); shared.loadError = error; break }
  }
  // Explicit end-of-download report — see the matching comment in load().
  if (shared.onProgress && shared.totalDownloadBytes && !shared.loadError) {
    shared.onProgress(new ProgressEvent('progress', { lengthComputable: true, loaded: shared.totalDownloadBytes, total: shared.totalDownloadBytes }))
  }
  if (bytesDownloaded - bytesProcessed > 0) {
    try {
      let concatenatedChunks = new Uint8Array(chunks.reduce((acc, chunk) => acc + chunk.length, 0))
      let offset = 0
      for (const chunk of chunks) { concatenatedChunks.set(chunk, offset); offset += chunk.length }
      let numVertices = Math.floor(concatenatedChunks.byteLength / shared.rowLength)
      // Unlike the in-loop push above, this leftover tail was never sliced
      // down to an exact multiple of rowLength before being handed to
      // pushDataBuffer (which reinterprets the WHOLE buffer as Float32s) —
      // on a complete, uninterrupted download the tail always happens to
      // already be a clean multiple (a valid file's total size always is),
      // so this went unnoticed. An interrupted download stops at an
      // arbitrary byte count instead, and pushDataBuffer throws
      // ("ArrayBuffer length ... not a multiple of the element size") on
      // the trailing partial-vertex bytes. Trim them off — a partial vertex
      // can't be reconstructed anyway.
      const aligned = concatenatedChunks.subarray(0, numVertices * shared.rowLength)
      const { buf, kept } = decimate(aligned, numVertices)
      if (kept > 0) {
        const matrices = pushDataBuffer(shared, buf, kept)
        if (!shared.gpuSort) shared.worker.postMessage({ method: 'push', src: shared.url, length: shared.numVertices * 16, matrices: matrices.buffer }, [matrices.buffer])
      }
    } catch (error) {
      console.error(error)
      if (!shared.loadError) shared.loadError = error
    }
  }
  // Defends against a truncated connection that reports itself as a clean
  // "done" (no thrown error at all — e.g. some proxies/CDNs do this on a
  // mid-transfer cutoff) rather than rejecting the read. A genuine stream
  // that finished normally should always have delivered the full expected
  // byte count.
  if (!shared.loadError && shared.totalDownloadBytes && bytesDownloaded < shared.totalDownloadBytes) {
    shared.loadError = new Error(`Splat stream ended early: received ${bytesDownloaded} of ${shared.totalDownloadBytes} bytes`)
    console.error(shared.loadError)
  }
  // WebGPU: final upload of everything pushed (pushDataBuffer only uploads
  // periodically while streaming).
  if ((shared.gl as any).isWebGPURenderer) {
    shared.centerAndScaleTexture.needsUpdate = true
    shared.covAndColorTexture.needsUpdate = true
  }
  shared.loaded = true
  shared.manager.itemEnd(shared.url)
}

// ─── PLY deferred GPU upload ──────────────────────────────────────────────────

async function lazyLoadPLY(shared: SharedState) {
  shared.loading = true
  // WebGPU migration Stage 2 (?webgpu=1): __webglTexture never exists under
  // WebGPURenderer.properties (its internal texture tracking has a
  // different shape) — this loop would otherwise spin forever. The
  // standard DataTexture.needsUpdate path (uploadAllTextures' WebGPU
  // branch) doesn't need a manual "wait until actually uploaded" dance the
  // way the private-internals bypass did — the renderer handles it
  // transparently on its next render() call.
  if (!(shared.gl as any).isWebGPURenderer) {
    while (true) {
      const p1 = shared.gl.properties.get(shared.centerAndScaleTexture)
      const p2 = shared.gl.properties.get(shared.covAndColorTexture)
      const p3 = shared.shTexture ? shared.gl.properties.get(shared.shTexture) : { __webglTexture: true }
      if (p1?.__webglTexture && p2?.__webglTexture && p3?.__webglTexture) break
      await new Promise((r) => setTimeout(r, 10))
    }
  }
  const { headerLength, rowLength, propOffsets, propTypes, fRestScale } = shared.plyMeta!
  const matrices = await pushPLYData(shared, shared.plyBuffer!, headerLength, rowLength, propOffsets, propTypes, fRestScale)
  if (!shared.gpuSort) shared.worker.postMessage({ method: 'push', src: shared.url, length: shared.numVertices * 16, matrices: matrices.buffer }, [matrices.buffer])
  shared.plyBuffer = null
  shared.loaded = true
  shared.manager.itemEnd(shared.url)
}

// ─── Chunked texture upload (avoids iOS WebGL limits on large single uploads) ─

function uploadTexture(
  shared: SharedState,
  context: WebGL2RenderingContext,
  tex: THREE.DataTexture,
  data: ArrayBufferView,
  fmt: number,
  type: number,
  totalHeight: number
) {
  const props = shared.gl.properties.get(tex)
  context.bindTexture(context.TEXTURE_2D, props.__webglTexture)
  const elemPerRow = shared.bufferTextureWidth * 4
  const chunkRows = 256
  for (let y = 0; y < totalHeight; y += chunkRows) {
    const h = Math.min(chunkRows, totalHeight - y)
    context.texSubImage2D(
      context.TEXTURE_2D, 0, 0, y,
      shared.bufferTextureWidth, h,
      fmt, type, data as any, y * elemPerRow
    )
  }
}

// WebGPU migration Stage 2 (?webgpu=1): the chunked uploadTexture() above
// reaches into WebGLRenderer's private internals
// (renderer.properties.get(tex).__webglTexture + raw bindTexture/
// texSubImage2D) specifically because plain DataTexture.needsUpdate was too
// slow for this — see uploadAllTextures' call sites' "single large calls
// can fail on iOS" comment. WebGPURenderer has no equivalent private
// texture handle (WebGPU textures are opaque GPUTexture objects), so under
// it this just falls back to the standard, portable three.js pipeline
// (tex.needsUpdate = true, letting the renderer do a normal full upload).
// This is NOT the fast/chunked path — it's a correctness-first stopgap so
// Stage 2 (the TSL shader) has real data to render against at all. A
// WebGPU-native fast path (GPUQueue.writeTexture, matching what the
// existing hack achieves for WebGL2) is a later-stage optimization, not
// needed to prove the shader itself works.
function uploadAllTextures(shared: SharedState) {
  shared.loadedVertexCount = shared.numVertices
  if ((shared.gl as any).isWebGPURenderer) {
    shared.centerAndScaleTexture.needsUpdate = true
    shared.covAndColorTexture.needsUpdate = true
    if (shared.hasSH && shared.shTexture) shared.shTexture.needsUpdate = true
    publishDebugStats(shared, true)
    // NOT freeStagingArrays() here, unlike the WebGL2 branch below —
    // createWebGPUSplatMesh's buildGaussianSplatGeometry still needs to
    // read shared.centerAndScaleData/covAndColorData directly (it builds
    // its own BufferGeometry snapshot from them, not from these textures),
    // and that happens later, after shared.loaded flips true. It frees
    // them itself once it's actually done reading.
    return
  }
  const context = shared.gl.getContext() as WebGL2RenderingContext
  uploadTexture(shared, context, shared.centerAndScaleTexture, shared.centerAndScaleData, context.RGBA, context.FLOAT, shared.bufferTextureHeight)
  // @ts-ignore
  uploadTexture(shared, context, shared.covAndColorTexture, shared.covAndColorData, context.RGBA_INTEGER, context.UNSIGNED_INT, shared.bufferTextureHeight)
  if (shared.hasSH && shared.shTexture) {
    uploadTexture(shared, context, shared.shTexture, shared.shData!, context.RGBA, context.HALF_FLOAT, shared.bufferTextureHeight * shRowsForDegree(shared.shDegree))
  }
  shared.gl.resetState()
  publishDebugStats(shared, true)
  freeStagingArrays(shared)
}

// ─── PLY binary parsing + GPU upload ─────────────────────────────────────────

// Async + chunked for the same reason as pushSOGData (see there).
async function pushPLYData(
  shared: SharedState,
  buffer: ArrayBuffer,
  headerLength: number,
  rowLength: number,
  propOffsets: Record<string, number>,
  propTypes: Record<string, string> = {},
  fRestScale = 1.0
): Promise<Float32Array> {
  const dataView = new DataView(buffer, headerLength)
  const vertexCount = shared.numVertices
  // Only the CPU sort worker reads these — none needed on the GPU-sort path.
  const matrices = new Float32Array(shared.gpuSort ? 0 : vertexCount * 16)
  const covAndColorData_uint8 = new Uint8Array(shared.covAndColorData.buffer)
  const covAndColorData_int16 = new Int16Array(shared.covAndColorData.buffer)
  const W = shared.bufferTextureWidth

  // Maps the output row (0..vertexCount) to a source row — identity at 100%
  // splat count, a uniform every-Nth pick when trimmed.
  const srcRow = srcIndexMapper(shared)

  const get = (row: number, prop: string): number => {
    const off = propOffsets[prop]
    if (off === undefined) return 0
    const base = srcRow(row) * rowLength + off
    const type = propTypes[prop]
    if (type === 'char') return dataView.getInt8(base) * fRestScale
    if (type === 'short') return dataView.getInt16(base, true) * fRestScale
    return dataView.getFloat32(base, true)
  }

  const shRows = shRowsForDegree(shared.shDegree)

  // Pack 4 SH coefficients as fp16 into this gaussian's RGBA16F texel row.
  const setSHRow = (i: number, rowOffset: number, a: number, b: number, c: number, d: number) => {
    const col = i % W
    const row = Math.floor(i / W) * shRows + rowOffset
    const base = (row * W + col) * 4
    shared.shData![base]     = toHalf(a)
    shared.shData![base + 1] = toHalf(b)
    shared.shData![base + 2] = toHalf(c)
    shared.shData![base + 3] = toHalf(d)
  }

  const _q = new THREE.Quaternion()
  const _center = new THREE.Vector3()
  const _scale = new THREE.Vector3()
  const _mtx = new THREE.Matrix4()
  const _mtxT = new THREE.Matrix4()
  const _col = new THREE.Color()
  const cov_indexes = [0, 1, 2, 5, 6, 10]
  const SH_C0 = 0.28209479177387814

  let lastYield = performance.now()
  for (let i = 0; i < vertexCount; i++) {
    if ((i & 4095) === 0 && performance.now() - lastYield > 30) {
      shared.onStageProgress?.('push', i / vertexCount)
      await yieldToMain()
      lastYield = performance.now()
    }
    const px = get(i, 'x'), py = get(i, 'y'), pz = get(i, 'z')
    const sx = Math.exp(get(i, 'scale_0'))
    const sy = Math.exp(get(i, 'scale_1'))
    const sz = Math.exp(get(i, 'scale_2'))
    // 3DGS convention: rot_0=w, rot_1=x, rot_2=y, rot_3=z
    const qw = get(i, 'rot_0'), qx = get(i, 'rot_1'), qy = get(i, 'rot_2'), qz = get(i, 'rot_3')

    _q.set(-qx, qy, qz, -qw)
    _q.invert()
    _center.set(px, py, -pz)
    _scale.set(sx, sy, sz)

    _mtx.makeRotationFromQuaternion(_q)
    _mtx.transpose()
    _mtx.scale(_scale)
    _mtxT.copy(_mtx)
    _mtx.transpose()
    _mtx.premultiply(_mtxT)
    _mtx.setPosition(_center)

    let max_value = 0.0
    for (let j = 0; j < cov_indexes.length; j++)
      if (Math.abs(_mtx.elements[cov_indexes[j]]) > max_value) max_value = Math.abs(_mtx.elements[cov_indexes[j]])

    let destOffset = i * 4
    shared.centerAndScaleData[destOffset]     = _center.x
    shared.centerAndScaleData[destOffset + 1] = -_center.y
    shared.centerAndScaleData[destOffset + 2] = _center.z
    shared.centerAndScaleData[destOffset + 3] = max_value / 32767.0

    destOffset = i * 8
    for (let j = 0; j < cov_indexes.length; j++)
      covAndColorData_int16[destOffset + j] = (_mtx.elements[cov_indexes[j]] * 32767.0) / max_value

    const opacityRaw = get(i, 'opacity')
    const opacity = (1.0 / (1.0 + Math.exp(-opacityRaw))) * 255
    const r = Math.max(0, Math.min(255, (0.5 + SH_C0 * get(i, 'f_dc_0')) * 255))
    const g = Math.max(0, Math.min(255, (0.5 + SH_C0 * get(i, 'f_dc_1')) * 255))
    const b = Math.max(0, Math.min(255, (0.5 + SH_C0 * get(i, 'f_dc_2')) * 255))
    destOffset = (i * 4 + 3) * 4
    _col.setRGB(r / 255, g / 255, b / 255)
    _col.convertSRGBToLinear()
    covAndColorData_uint8[destOffset]     = _col.r * 255
    covAndColorData_uint8[destOffset + 1] = _col.g * 255
    covAndColorData_uint8[destOffset + 2] = _col.b * 255
    covAndColorData_uint8[destOffset + 3] = opacity

    if (shared.hasSH) {
      if (shared.shDegree === 1) {
        // 3 rows: one per channel with [dc, deg1_x, deg1_y, deg1_z]
        setSHRow(i, 0, get(i,'f_dc_0'), get(i,'f_rest_0'),  get(i,'f_rest_1'),  get(i,'f_rest_2'))
        setSHRow(i, 1, get(i,'f_dc_1'), get(i,'f_rest_15'), get(i,'f_rest_16'), get(i,'f_rest_17'))
        setSHRow(i, 2, get(i,'f_dc_2'), get(i,'f_rest_30'), get(i,'f_rest_31'), get(i,'f_rest_32'))
      } else if (shared.shDegree === 2) {
        // 9 rows: 3 per channel, covering DC + degree1(3) + degree2(5);
        // last row's y/z/w unused (padding, degree-3 terms not stored)
        setSHRow(i, 0, get(i,'f_dc_0'),    get(i,'f_rest_0'),  get(i,'f_rest_1'),  get(i,'f_rest_2'))
        setSHRow(i, 1, get(i,'f_rest_3'),  get(i,'f_rest_4'),  get(i,'f_rest_5'),  get(i,'f_rest_6'))
        setSHRow(i, 2, get(i,'f_rest_7'),  0, 0, 0)
        setSHRow(i, 3, get(i,'f_dc_1'),    get(i,'f_rest_15'), get(i,'f_rest_16'), get(i,'f_rest_17'))
        setSHRow(i, 4, get(i,'f_rest_18'), get(i,'f_rest_19'), get(i,'f_rest_20'), get(i,'f_rest_21'))
        setSHRow(i, 5, get(i,'f_rest_22'), 0, 0, 0)
        setSHRow(i, 6, get(i,'f_dc_2'),    get(i,'f_rest_30'), get(i,'f_rest_31'), get(i,'f_rest_32'))
        setSHRow(i, 7, get(i,'f_rest_33'), get(i,'f_rest_34'), get(i,'f_rest_35'), get(i,'f_rest_36'))
        setSHRow(i, 8, get(i,'f_rest_37'), 0, 0, 0)
      } else {
        // Full degree-3 SH — 3DGS channel-major: R=f_rest_0..14, G=15..29, B=30..44
        setSHRow(i, 0,  get(i,'f_dc_0'),    get(i,'f_rest_0'),  get(i,'f_rest_1'),  get(i,'f_rest_2'))
        setSHRow(i, 1,  get(i,'f_rest_3'),  get(i,'f_rest_4'),  get(i,'f_rest_5'),  get(i,'f_rest_6'))
        setSHRow(i, 2,  get(i,'f_rest_7'),  get(i,'f_rest_8'),  get(i,'f_rest_9'),  get(i,'f_rest_10'))
        setSHRow(i, 3,  get(i,'f_rest_11'), get(i,'f_rest_12'), get(i,'f_rest_13'), get(i,'f_rest_14'))
        setSHRow(i, 4,  get(i,'f_dc_1'),    get(i,'f_rest_15'), get(i,'f_rest_16'), get(i,'f_rest_17'))
        setSHRow(i, 5,  get(i,'f_rest_18'), get(i,'f_rest_19'), get(i,'f_rest_20'), get(i,'f_rest_21'))
        setSHRow(i, 6,  get(i,'f_rest_22'), get(i,'f_rest_23'), get(i,'f_rest_24'), get(i,'f_rest_25'))
        setSHRow(i, 7,  get(i,'f_rest_26'), get(i,'f_rest_27'), get(i,'f_rest_28'), get(i,'f_rest_29'))
        setSHRow(i, 8,  get(i,'f_dc_2'),    get(i,'f_rest_30'), get(i,'f_rest_31'), get(i,'f_rest_32'))
        setSHRow(i, 9,  get(i,'f_rest_33'), get(i,'f_rest_34'), get(i,'f_rest_35'), get(i,'f_rest_36'))
        setSHRow(i, 10, get(i,'f_rest_37'), get(i,'f_rest_38'), get(i,'f_rest_39'), get(i,'f_rest_40'))
        setSHRow(i, 11, get(i,'f_rest_41'), get(i,'f_rest_42'), get(i,'f_rest_43'), get(i,'f_rest_44'))
      }
    }

    _mtx.elements[15] = (Math.max(sx, sy, sz) * opacity) / 255.0
    for (let j = 0; j < 16; j++) matrices[i * 16 + j] = _mtx.elements[j]
  }

  // Upload all textures to GPU in chunks (single large calls can fail on iOS)
  // — see uploadAllTextures for the WebGPU branch.
  uploadAllTextures(shared)
  return matrices
}

// ─── Frame update ─────────────────────────────────────────────────────────────

// Static path only. Profiled 2026-09-30 (Chrome trace + embedded V8 CPU
// profile, reconstructed from ProfileChunk samples): on the MAIN thread,
// 64.8% of ALL frame time was a single call — bufferSubData, uploading the
// freshly-sorted splat-index buffer to the GPU after every sort (see the
// listener() in createTargetMesh: attr.needsUpdate = true triggers it). The
// sort worker itself was only 30% busy, mostly idle otherwise. Frustum
// culling (tried instead, three separate attempts) kept causing real
// scene content to visibly disappear each time despite passing isolated
// math tests — reverted for good; this throttle is the safe alternative:
// it never excludes any splat, ever, so there is zero risk of a visual
// regression — it only delays how often the (unchanged, full) sorted order
// is re-uploaded. SORT_THROTTLE_FRAMES re-sorts/re-uploads every Nth frame,
// rendering with the last valid sorted order (already on the GPU) on
// skipped frames — stale by at most N-1 frames, not a visible gap.
const SORT_THROTTLE_FRAMES = 2
const _sortMvp = new THREE.Matrix4()

// Streamed sort's off-screen cull margin. A draw order is shown from when it
// lands until the next one does, so while the camera turns the view runs
// ahead of the area the order covers. A fixed 1.6 × w margin (2026-10-21) was
// only ~13° past the edge with a wide lens: a quick turn outran it, and the
// side being turned toward went dark (background) in a straight-edged wedge
// until the next sort landed — the "flickering patches" while moving on
// desktop (2026-10-30). The margin is now an angle past each screen edge:
// SORT_CULL_BASE_DEG at rest, plus how far the camera turns while an order
// is on screen (turn rate × twice the sort latency, + a frame, × 1.25).
// Past SORT_CULL_MAX_DEG off-axis there's no cull on that axis at all.
const SORT_CULL_BASE_DEG = 10
const SORT_CULL_MAX_DEG = 80
const _camDir = new THREE.Vector3()

// Camera turn rate (rad/s) on the target, every frame: jumps up at once,
// decays over ~0.3 s so a margin stays wide through a jerky turn.
function trackTurnRate(target: TargetMesh, camera: THREE.Camera) {
  const t: any = target
  const now = performance.now()
  camera.getWorldDirection(_camDir)
  if (t.turnDir && t.turnAt) {
    const dt = Math.max((now - t.turnAt) / 1000, 1e-3)
    const angle = Math.acos(Math.min(1, Math.max(-1, _camDir.dot(t.turnDir))))
    t.turnRate = Math.max(angle / dt, (t.turnRate || 0) * Math.exp(-dt / 0.3))
  } else {
    t.turnDir = new THREE.Vector3()
  }
  t.turnDir.copy(_camDir)
  t.turnAt = now
}

function sortCullMargins(target: TargetMesh, camera: THREE.Camera): { marginX: number; marginY: number } {
  const p = camera.projectionMatrix.elements
  if (p[11] !== -1) return { marginX: 1.6, marginY: 1.6 } // not a perspective camera
  const t: any = target
  const lead = (t.turnRate || 0) * (2 * (t.sortLatency ?? 0.1) + 0.016) * 1.25
  const extra = THREE.MathUtils.degToRad(SORT_CULL_BASE_DEG) + lead
  const max = THREE.MathUtils.degToRad(SORT_CULL_MAX_DEG)
  const margin = (tanHalf: number) => {
    const a = Math.atan(tanHalf) + extra
    return a >= max ? Infinity : Math.max(1.3, Math.tan(a) / tanHalf)
  }
  return { marginX: margin(1 / p[0]), marginY: margin(1 / p[5]) }
}
// Streamed models: splats whose on-screen size (std dev, px) × opacity is
// below this are left out of the draw. ?subpixel=<px> overrides (0 = off).
// 0.3 visibly thinned far areas on an iPad; 0.15 keeps most of the gain.
const STREAM_SUBPIXEL_PX = (() => {
  const v = typeof window !== 'undefined' ? parseFloat(new URLSearchParams(window.location.search).get('subpixel') || '') : NaN
  return Number.isFinite(v) && v >= 0 ? v : 0.15
})()
// Streamed draw target (splatOctree.js visibleCap) — splats ON SCREEN:
// phones / tablets 1.3M, desktop none. Measured 2026-10-23 (stream test page,
// WebKit 26, heavy view of a 6.5M model): 1.88M → 1.21M drawn, GPU per frame
// −28%, mean pixel difference 0.6/255 (0.27% of pixels > 8/255). The first
// version (a 1.2M target that counted whole frustum-touching boxes) drew far
// fewer and visibly coarsened rooftops. ?drawCap=<splats> overrides, 0 = none.
// Quality menu's Performance switch (per browser, off by default): draws at
// most STREAM_PERFORMANCE_CAP splats on screen. Off by default because with
// thinned coarse LOD levels a cap shows rooftop holes.
export const STREAM_PERFORMANCE_KEY = 'streamPerformance'
const STREAM_PERFORMANCE_CAP = 1_300_000
export function isStreamPerformanceOn(): boolean {
  try { return localStorage.getItem(STREAM_PERFORMANCE_KEY) === '1' } catch (e) { return false }
}
export function streamPerformanceCap(on: boolean): number {
  return on ? STREAM_PERFORMANCE_CAP : Infinity
}

// ?drawCap= (0 = unlimited) overrides the Performance switch.
function streamDrawCap(): number {
  const v = typeof window !== 'undefined' ? parseFloat(new URLSearchParams(window.location.search).get('drawCap') || '') : NaN
  if (Number.isFinite(v)) return v > 0 ? v : Infinity
  return streamPerformanceCap(isStreamPerformanceOn())
}

// Exported (no behavior change for the streamed path) so it can reuse this
// unchanged — it only builds a view vector and posts a 'sort' message; the
// stride-16-vs-stride-4 matrices layout difference lives entirely inside
// each worker's own sortSplats, not here. Throttling is gated on
// `!shared.octree` (only set by loadStreamed) — the streamed octree already
// has its own budget/solve cadence in splatOctree.js and wasn't part of
// this investigation; scoped to static only, per instruction.
const _drawingBufferSize = new THREE.Vector2()

export function update(camera: THREE.Camera, shared: SharedState, target: TargetMesh, hashed: boolean) {
  camera.updateMatrixWorld()
  // WebGPU migration Stage 1 (?webgpu=1): WebGPURenderer has no
  // getCurrentViewport — splats aren't expected to render correctly yet
  // under it (that's Stage 2, the GLSL->TSL shader rewrite), but this guard
  // keeps the skeleton from throwing so Stage 1 can be verified end-to-end.
  // @ts-ignore
  if (shared.gl.getCurrentViewport) shared.gl.getCurrentViewport(target.viewport)
  else {
    // WebGPURenderer has no getCurrentViewport — same physical-pixel size
    // from the drawing buffer.
    // @ts-ignore
    shared.gl.getDrawingBufferSize(_drawingBufferSize)
    target.viewport.set(0, 0, _drawingBufferSize.x, _drawingBufferSize.y)
  }
  // @ts-ignore
  target.material.viewport.x = target.viewport.z
  // @ts-ignore
  target.material.viewport.y = target.viewport.w
  target.material.focal = (target.viewport.w / 2.0) * Math.abs(camera.projectionMatrix.elements[5])
  if ((shared as any).octree) trackTurnRate(target, camera)
  if (target.ready) {
    // ?perfNoSort=1 test (SceneHandler): keep the first draw orders, no re-sorts.
    if ((window as any).__perfNoSort && ((target as any).sortsLanded || 0) >= 3) return
    if (hashed && target.sorted) return
    if (!hashed && !(shared as any).octree) {
      target.sortSkipCount = (target.sortSkipCount || 0) + 1
      if (target.sortSkipCount % SORT_THROTTLE_FRAMES !== 0) return
    }
    target.ready = false
    // Both renderers only refresh object.modelViewMatrix during the draw
    // (after onBeforeRender, where this runs) — compute it here so the sort
    // never uses last frame's (or, on frame one, an identity) matrix.
    if ((shared.gl as any).isWebGPURenderer || (shared as any).octree) {
      target.modelViewMatrix.multiplyMatrices((camera as any).matrixWorldInverse, target.matrixWorld)
    }
    const mv = target.modelViewMatrix.elements
    const view = new Float32Array([
      mv[2], -mv[6], mv[10], mv[14],
    ])
    const density = (target.material as any).density ?? 1
    if ((shared as any).octree) {
      // Streamed: also the clip-space x / y / w rows for the sort's
      // off-screen cull. Sort data stores y un-negated while the shader's
      // centres are (x, -y, z) — hence the negated y column (same as -mv[6]
      // in the depth row above).
      const e = _sortMvp.multiplyMatrices((camera as any).projectionMatrix, target.modelViewMatrix).elements
      const frustum = new Float32Array([
        e[0], -e[4], e[8], e[12],
        e[1], -e[5], e[9], e[13],
        e[3], -e[7], e[11], e[15],
      ])
      // Sub-pixel cull threshold (see the worker): SUBPIXEL_PX / focal (px).
      const focalPx = (target.material as any).focal || 0
      const minPxDist = focalPx > 0 ? STREAM_SUBPIXEL_PX / focalPx : 0
      const { marginX, marginY } = sortCullMargins(target, camera)
      ;(target as any).sortSentAt = performance.now()
      // Freed pool blocks wait for the sort after this to land (splatPool.js quarantineDone).
      ;(shared as any).pool.sortsPosted++
      shared.worker.postMessage({ method: 'sort', src: shared.url, key: target.uuid, view: view.buffer, frustum: frustum.buffer, hashed, density, minPxDist, marginX, marginY }, [view.buffer, frustum.buffer])
    } else {
      shared.worker.postMessage({ method: 'sort', src: shared.url, key: target.uuid, view: view.buffer, hashed, density }, [view.buffer])
    }
    if (hashed && shared.loaded) target.sorted = true
  }
}

// ─── SOG GPU upload ───────────────────────────────────────────────────────────

// Yields to the event loop so the browser can repaint (e.g. the loading
// ring) mid-way through long synchronous work. MessageChannel rather than
// setTimeout(0): background tabs clamp timers to ~1 s, which would stretch
// a chunked loop by a second per chunk; message tasks aren't clamped.
function yieldToMain(): Promise<void> {
  return new Promise((resolve) => {
    const ch = new MessageChannel()
    ch.port1.onmessage = () => { ch.port1.close(); resolve() }
    ch.port2.postMessage(null)
  })
}

// Async + chunked: for a multi-million-splat SOG this loop ran ~1 s+ as one
// block, freezing the loading ring mid-way. Yields every ~30 ms of work.
async function pushSOGData(shared: SharedState, dataTable: { cols: Record<string, Float32Array>; numRows: number }): Promise<Float32Array> {
  const vertexCount = shared.numVertices
  // Only the CPU sort worker reads these — none needed on the GPU-sort path.
  const matrices = new Float32Array(shared.gpuSort ? 0 : vertexCount * 16)
  const covAndColorData_uint8 = new Uint8Array(shared.covAndColorData.buffer)
  const covAndColorData_int16 = new Int16Array(shared.covAndColorData.buffer)
  const W = shared.bufferTextureWidth

  const getCol = (name: string): Float32Array =>
    (dataTable.cols[name] as Float32Array) ?? new Float32Array(vertexCount)

  const cx = getCol('x'), cy = getCol('y'), cz = getCol('z')
  const s0 = getCol('scale_0'), s1 = getCol('scale_1'), s2 = getCol('scale_2')
  const r0 = getCol('rot_0'), r1 = getCol('rot_1'), r2 = getCol('rot_2'), r3 = getCol('rot_3')
  const op = getCol('opacity')
  const dc0 = getCol('f_dc_0'), dc1 = getCol('f_dc_1'), dc2 = getCol('f_dc_2')
  const fRest = Array.from({ length: 45 }, (_, i) => getCol(`f_rest_${i}`))

  const shRows = shRowsForDegree(shared.shDegree)
  const setSHRow = (i: number, rowOffset: number, a: number, b: number, c: number, d: number) => {
    const col = i % W
    const row = Math.floor(i / W) * shRows + rowOffset
    const base = (row * W + col) * 4
    shared.shData![base] = toHalf(a); shared.shData![base + 1] = toHalf(b)
    shared.shData![base + 2] = toHalf(c); shared.shData![base + 3] = toHalf(d)
  }

  const _q = new THREE.Quaternion()
  const _center = new THREE.Vector3()
  const _scale = new THREE.Vector3()
  const _mtx = new THREE.Matrix4()
  const _mtxT = new THREE.Matrix4()
  const _col = new THREE.Color()
  const cov_indexes = [0, 1, 2, 5, 6, 10]
  const SH_C0 = 0.28209479177387814

  let lastYield = performance.now()
  for (let i = 0; i < vertexCount; i++) {
    if ((i & 4095) === 0 && performance.now() - lastYield > 30) {
      shared.onStageProgress?.('push', i / vertexCount)
      await yieldToMain()
      lastYield = performance.now()
    }
    const px = cx[i], py = cy[i], pz = cz[i]
    const sx = Math.exp(s0[i]), sy = Math.exp(s1[i]), sz = Math.exp(s2[i])
    const qw = r0[i], qx = r1[i], qy = r2[i], qz = r3[i]

    _q.set(-qx, qy, qz, -qw)
    _q.invert()
    _center.set(px, py, -pz)
    _scale.set(sx, sy, sz)

    _mtx.makeRotationFromQuaternion(_q)
    _mtx.transpose()
    _mtx.scale(_scale)
    _mtxT.copy(_mtx)
    _mtx.transpose()
    _mtx.premultiply(_mtxT)
    _mtx.setPosition(_center)

    let max_value = 0.0
    for (let j = 0; j < cov_indexes.length; j++)
      if (Math.abs(_mtx.elements[cov_indexes[j]]) > max_value) max_value = Math.abs(_mtx.elements[cov_indexes[j]])

    let destOffset = i * 4
    shared.centerAndScaleData[destOffset]     = _center.x
    shared.centerAndScaleData[destOffset + 1] = -_center.y
    shared.centerAndScaleData[destOffset + 2] = _center.z
    shared.centerAndScaleData[destOffset + 3] = max_value / 32767.0

    destOffset = i * 8
    for (let j = 0; j < cov_indexes.length; j++)
      covAndColorData_int16[destOffset + j] = (_mtx.elements[cov_indexes[j]] * 32767.0) / max_value

    const opacityRaw = op[i]
    const opacity = (1.0 / (1.0 + Math.exp(-opacityRaw))) * 255
    const r = Math.max(0, Math.min(255, (0.5 + SH_C0 * dc0[i]) * 255))
    const g = Math.max(0, Math.min(255, (0.5 + SH_C0 * dc1[i]) * 255))
    const b = Math.max(0, Math.min(255, (0.5 + SH_C0 * dc2[i]) * 255))
    destOffset = (i * 4 + 3) * 4
    _col.setRGB(r / 255, g / 255, b / 255)
    _col.convertSRGBToLinear()
    covAndColorData_uint8[destOffset]     = _col.r * 255
    covAndColorData_uint8[destOffset + 1] = _col.g * 255
    covAndColorData_uint8[destOffset + 2] = _col.b * 255
    covAndColorData_uint8[destOffset + 3] = opacity

    if (shared.hasSH) {
      if (shared.shDegree === 1) {
        // 3 rows: one per channel with [dc, deg1_x, deg1_y, deg1_z]
        setSHRow(i, 0, dc0[i], fRest[0][i],  fRest[1][i],  fRest[2][i])
        setSHRow(i, 1, dc1[i], fRest[15][i], fRest[16][i], fRest[17][i])
        setSHRow(i, 2, dc2[i], fRest[30][i], fRest[31][i], fRest[32][i])
      } else if (shared.shDegree === 2) {
        // 9 rows: 3 per channel, covering DC + degree1(3) + degree2(5);
        // last row's y/z/w unused (padding, degree-3 terms not stored)
        setSHRow(i, 0, dc0[i],         fRest[0][i],  fRest[1][i],  fRest[2][i])
        setSHRow(i, 1, fRest[3][i],    fRest[4][i],  fRest[5][i],  fRest[6][i])
        setSHRow(i, 2, fRest[7][i],    0, 0, 0)
        setSHRow(i, 3, dc1[i],         fRest[15][i], fRest[16][i], fRest[17][i])
        setSHRow(i, 4, fRest[18][i],   fRest[19][i], fRest[20][i], fRest[21][i])
        setSHRow(i, 5, fRest[22][i],   0, 0, 0)
        setSHRow(i, 6, dc2[i],         fRest[30][i], fRest[31][i], fRest[32][i])
        setSHRow(i, 7, fRest[33][i],   fRest[34][i], fRest[35][i], fRest[36][i])
        setSHRow(i, 8, fRest[37][i],   0, 0, 0)
      } else {
        setSHRow(i, 0,  dc0[i],         fRest[0][i],  fRest[1][i],  fRest[2][i])
        setSHRow(i, 1,  fRest[3][i],    fRest[4][i],  fRest[5][i],  fRest[6][i])
        setSHRow(i, 2,  fRest[7][i],    fRest[8][i],  fRest[9][i],  fRest[10][i])
        setSHRow(i, 3,  fRest[11][i],   fRest[12][i], fRest[13][i], fRest[14][i])
        setSHRow(i, 4,  dc1[i],         fRest[15][i], fRest[16][i], fRest[17][i])
        setSHRow(i, 5,  fRest[18][i],   fRest[19][i], fRest[20][i], fRest[21][i])
        setSHRow(i, 6,  fRest[22][i],   fRest[23][i], fRest[24][i], fRest[25][i])
        setSHRow(i, 7,  fRest[26][i],   fRest[27][i], fRest[28][i], fRest[29][i])
        setSHRow(i, 8,  dc2[i],         fRest[30][i], fRest[31][i], fRest[32][i])
        setSHRow(i, 9,  fRest[33][i],   fRest[34][i], fRest[35][i], fRest[36][i])
        setSHRow(i, 10, fRest[37][i],   fRest[38][i], fRest[39][i], fRest[40][i])
        setSHRow(i, 11, fRest[41][i],   fRest[42][i], fRest[43][i], fRest[44][i])
      }
    }

    _mtx.elements[15] = (Math.max(sx, sy, sz) * opacity) / 255.0
    for (let j = 0; j < 16; j++) matrices[i * 16 + j] = _mtx.elements[j]
  }

  uploadAllTextures(shared)
  return matrices
}

async function lazyLoadSOG(shared: SharedState) {
  shared.loading = true
  // See lazyLoadPLY's matching comment — WebGPU skips this poll entirely.
  if (!(shared.gl as any).isWebGPURenderer) {
    while (true) {
      const p1 = shared.gl.properties.get(shared.centerAndScaleTexture)
      const p2 = shared.gl.properties.get(shared.covAndColorTexture)
      const p3 = shared.hasSH && shared.shTexture ? shared.gl.properties.get(shared.shTexture) : { __webglTexture: true }
      if (p1?.__webglTexture && p2?.__webglTexture && p3?.__webglTexture) break
      await new Promise((r) => setTimeout(r, 10))
    }
  }
  const matrices = await pushSOGData(shared, shared.sogDataTable)
  if (!shared.gpuSort) shared.worker.postMessage({ method: 'push', src: shared.url, length: shared.numVertices * 16, matrices: matrices.buffer }, [matrices.buffer])
  shared.sogDataTable = null
  shared.loaded = true
  shared.manager.itemEnd(shared.url)
}

// ─── Connect mesh to shared state ────────────────────────────────────────────

// Exported (no behavior change) so loadStreamed() below can reuse it as-is:
// setting shared.loading = true before calling this skips the one-shot
// sogDataTable/plyBuffer/lazyLoad dispatch branch entirely (streaming has
// no such one-shot load phase — SplatOctree drives residency continuously),
// leaving the generic mesh/geometry/texture-readiness wiring, which only
// depends on shared.bufferTextureWidth/Height, .worker, .gl, the three
// textures, .hasSH and .loadedVertexCount — all of which loadStreamed()
// populates from the SplatPool.
// Kicks off the actual deferred data parsing + GPU upload — until this
// runs, shared.loaded stays false forever (confirmed live: WebGPU path
// hung at 99% with zero errors, because nothing was calling this at all).
// Split out of connect() below so createWebGPUSplatMesh can trigger it
// without connect()'s other half, which builds OUR GLSL-specific
// TargetMesh geometry/worker-listener machinery — irrelevant to
// GaussianSplat, which brings its own geometry and its own GPU sort.
export function triggerLazyLoad(shared: SharedState): Promise<void> | undefined {
  if (!shared.loading) {
    if (shared.sogDataTable) return lazyLoadSOG(shared)
    else if (shared.plyBuffer) return lazyLoadPLY(shared)
    else return lazyLoad(shared)
  }
  return undefined
}

export function connect(shared: SharedState, target: TargetMesh) {
  triggerLazyLoad(shared)

  target.ready = false
  target.pm = new THREE.Matrix4()
  target.vm1 = new THREE.Matrix4()
  target.vm2 = new THREE.Matrix4()
  target.viewport = new THREE.Vector4()

  let splatIndexArray = new Uint32Array(shared.bufferTextureWidth * shared.bufferTextureHeight)
  const splatIndexes = new THREE.InstancedBufferAttribute(splatIndexArray, 1, false)
  splatIndexes.setUsage(THREE.DynamicDrawUsage)

  const geometry = (target.geometry = new THREE.InstancedBufferGeometry())
  // 4 shared corners + index (two counter-clockwise triangles, same as the
  // old 6 non-indexed vertices) — each corner's vertex shader runs once.
  geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
    -2, -2, 0, 2, -2, 0, 2, 2, 0, -2, 2, 0,
  ]), 3))
  geometry.setIndex([0, 1, 2, 0, 2, 3])
  geometry.setAttribute('splatIndex', splatIndexes)
  geometry.instanceCount = 1

  function listener(e: {
    data: { key: string; indices: Uint32Array; count?: number; sortMs?: number; density?: number }
  }) {
    if (target && e.data.key === target.uuid) {
      const tIn0 = performance.now()
      // Streamed sort worker sends a capacity-sized buffer + a `count`
      // (valid prefix) and expects the buffer handed back for reuse. The
      // legacy createWorker sends an exact-length `indices` and no `count`.
      const streaming = typeof e.data.count === 'number'
      const count = streaming ? (e.data.count! | 0) : e.data.indices.length
      const attr = geometry.attributes.splatIndex
      // @ts-ignore
      attr.set(streaming ? e.data.indices.subarray(0, count) : e.data.indices)
      // @ts-ignore
      attr.clearUpdateRanges()
      // @ts-ignore
      attr.addUpdateRange(0, count)
      // @ts-ignore
      attr.needsUpdate = true
      geometry.instanceCount = count
      ;(target as any).sortsLanded = ((target as any).sortsLanded || 0) + 1
      target.ready = true
      // Static (CPU sort): the splat share this draw order includes — a
      // raised share only shows its extra splats once a sort with it lands
      // (captures wait for that — see SceneHandler's withBestCaptureQuality).
      if (!streaming) (target as any).sortedDensity = e.data.density ?? 1
      if (streaming) {
        // This order no longer draws from blocks freed before its sort was
        // posted — they may be reused now (splatPool.js quarantineDone).
        if ((shared as any).pool) (shared as any).pool.sortsLanded++
        // Sort latency (send → land) for the cull margin in update(): jumps
        // up at once, eases down.
        const sentAt = (target as any).sortSentAt
        if (sentAt) {
          const lat = (tIn0 - sentAt) / 1000
          ;(target as any).sortLatency = Math.max(lat, ((target as any).sortLatency || 0) * 0.8)
        }
        const buf = e.data.indices.buffer
        // @ts-ignore
        shared.worker.postMessage({ method: 'recycle', buffer: buf }, [buf])
        if (typeof window !== 'undefined' && (window as any).__splatOctreeStats) {
          ;(window as any).__splatOctreeStats.sortMs = e.data.sortMs != null ? +e.data.sortMs.toFixed(1) : undefined
          ;(window as any).__splatOctreeStats.sortedSplats = count
        }
      }
      // Stutter readout (SceneHandler's DEBUG overlay): time spent taking in
      // sort results between frames.
      if (typeof window !== 'undefined') {
        const acc = (window as any).__jankAcc || ((window as any).__jankAcc = { ms: 0, n: 0 })
        acc.ms += performance.now() - tIn0
        acc.n += 1
      }
    }
  }
  shared.worker.addEventListener('message', listener)

  async function wait() {
    // See lazyLoadPLY's matching comment — WebGPU skips the __webglTexture
    // checks (never populated under it), keeping only the renderer-agnostic
    // loadedVertexCount readiness check.
    const isWebGPU = (shared.gl as any).isWebGPURenderer
    while (true) {
      if (isWebGPU) {
        if (shared.loadedVertexCount <= 0) { await new Promise((r) => setTimeout(r, 10)); continue }
        break
      }
      const p1 = shared.gl.properties.get(shared.centerAndScaleTexture)
      const p2 = shared.gl.properties.get(shared.covAndColorTexture)
      if (!p1?.__webglTexture || !p2?.__webglTexture || shared.loadedVertexCount <= 0) {
        await new Promise((r) => setTimeout(r, 10)); continue
      }
      if (shared.hasSH) {
        const p3 = shared.shTexture ? shared.gl.properties.get(shared.shTexture) : null
        if (!p3?.__webglTexture) { await new Promise((r) => setTimeout(r, 10)); continue }
      }
      break
    }
    target.ready = true
    // The GL textures are live now; the pool's CPU-side backing arrays
    // (hundreds of MB at a big budget) were only there to seed them — all
    // further writes go straight to VRAM. Release them.
    ;(shared as any).pool?.releaseInitBuffers?.()
  }

  wait()
  return () => shared.worker.removeEventListener('message', listener)
}

// ─── Splat binary parsing (unchanged) ────────────────────────────────────────

const WEBGPU_PARTIAL_UPLOAD_MS = 1500

function pushDataBuffer(shared: SharedState, buffer: ArrayBufferLike, vertexCount: number) {
  const context = shared.gl.getContext()
  if (shared.loadedVertexCount + vertexCount > shared.maxVertexes) vertexCount = shared.maxVertexes - shared.loadedVertexCount
  if (vertexCount <= 0) throw 'Failed to parse file'

  const u_buffer = new Uint8Array(buffer)
  const f_buffer = new Float32Array(buffer)
  // Only the CPU sort worker reads these — none needed on the GPU-sort path.
  const matrices = new Float32Array(shared.gpuSort ? 0 : vertexCount * 16)
  const covAndColorData_uint8 = new Uint8Array(shared.covAndColorData.buffer)
  const covAndColorData_int16 = new Int16Array(shared.covAndColorData.buffer)

  const _q = new THREE.Quaternion()
  const _center = new THREE.Vector3()
  const _scale = new THREE.Vector3()
  const _mtx = new THREE.Matrix4()
  const _mtxT = new THREE.Matrix4()
  const _col = new THREE.Color()
  const cov_indexes = [0, 1, 2, 5, 6, 10]

  for (let i = 0; i < vertexCount; i++) {
    _q.set(
      -(u_buffer[32 * i + 28 + 1] - 128) / 128.0,
      (u_buffer[32 * i + 28 + 2] - 128) / 128.0,
      (u_buffer[32 * i + 28 + 3] - 128) / 128.0,
      -(u_buffer[32 * i + 28 + 0] - 128) / 128.0
    )
    _q.invert()
    _center.set(f_buffer[8 * i + 0], f_buffer[8 * i + 1], -f_buffer[8 * i + 2])
    _scale.set(f_buffer[8 * i + 3 + 0], f_buffer[8 * i + 3 + 1], f_buffer[8 * i + 3 + 2])

    _mtx.makeRotationFromQuaternion(_q)
    _mtx.transpose()
    _mtx.scale(_scale)
    _mtxT.copy(_mtx)
    _mtx.transpose()
    _mtx.premultiply(_mtxT)
    _mtx.setPosition(_center)

    let max_value = 0.0
    for (let j = 0; j < cov_indexes.length; j++)
      if (Math.abs(_mtx.elements[cov_indexes[j]]) > max_value) max_value = Math.abs(_mtx.elements[cov_indexes[j]])

    let destOffset = shared.loadedVertexCount * 4 + i * 4
    shared.centerAndScaleData[destOffset]     = _center.x
    shared.centerAndScaleData[destOffset + 1] = -_center.y
    shared.centerAndScaleData[destOffset + 2] = _center.z
    shared.centerAndScaleData[destOffset + 3] = max_value / 32767.0

    destOffset = shared.loadedVertexCount * 8 + i * 4 * 2
    for (let j = 0; j < cov_indexes.length; j++)
      covAndColorData_int16[destOffset + j] = (_mtx.elements[cov_indexes[j]] * 32767.0) / max_value

    destOffset = shared.loadedVertexCount * 16 + (i * 4 + 3) * 4
    _col.setRGB(u_buffer[32 * i + 24 + 0] / 255, u_buffer[32 * i + 24 + 1] / 255, u_buffer[32 * i + 24 + 2] / 255)
    _col.convertSRGBToLinear()
    covAndColorData_uint8[destOffset]     = _col.r * 255
    covAndColorData_uint8[destOffset + 1] = _col.g * 255
    covAndColorData_uint8[destOffset + 2] = _col.b * 255
    covAndColorData_uint8[destOffset + 3] = u_buffer[32 * i + 24 + 3]

    _mtx.elements[15] = (Math.max(_scale.x, _scale.y, _scale.z) * u_buffer[32 * i + 24 + 3]) / 255.0
    for (let j = 0; j < 16; j++) matrices[i * 16 + j] = _mtx.elements[j]
  }

  // WebGPURenderer has no raw GL texture handles (renderer.properties /
  // texSubImage2D below are WebGL-only). The CPU arrays already hold this
  // chunk, so just count it and let three re-upload the textures — at most
  // every WEBGPU_PARTIAL_UPLOAD_MS while streaming (a full re-upload per
  // chunk would be quadratic), and once more when the load completes
  // (see the end of lazyLoad).
  if ((shared.gl as any).isWebGPURenderer) {
    shared.loadedVertexCount += vertexCount
    const now = performance.now()
    if (!(shared as any)._lastGpuUploadAt || now - (shared as any)._lastGpuUploadAt >= WEBGPU_PARTIAL_UPLOAD_MS) {
      ;(shared as any)._lastGpuUploadAt = now
      shared.centerAndScaleTexture.needsUpdate = true
      shared.covAndColorTexture.needsUpdate = true
    }
    return matrices
  }

  while (vertexCount > 0) {
    let width = 0, height = 0
    const xoffset = shared.loadedVertexCount % shared.bufferTextureWidth
    const yoffset = Math.floor(shared.loadedVertexCount / shared.bufferTextureWidth)
    if (shared.loadedVertexCount % shared.bufferTextureWidth != 0) {
      width = Math.min(shared.bufferTextureWidth, xoffset + vertexCount) - xoffset; height = 1
    } else if (Math.floor(vertexCount / shared.bufferTextureWidth) > 0) {
      width = shared.bufferTextureWidth; height = Math.floor(vertexCount / shared.bufferTextureWidth)
    } else {
      width = vertexCount % shared.bufferTextureWidth; height = 1
    }
    const csProps = shared.gl.properties.get(shared.centerAndScaleTexture)
    context.bindTexture(context.TEXTURE_2D, csProps.__webglTexture)
    context.texSubImage2D(context.TEXTURE_2D, 0, xoffset, yoffset, width, height, context.RGBA, context.FLOAT, shared.centerAndScaleData as any, shared.loadedVertexCount * 4)
    const ccProps = shared.gl.properties.get(shared.covAndColorTexture)
    context.bindTexture(context.TEXTURE_2D, ccProps.__webglTexture)
    // @ts-ignore
    context.texSubImage2D(context.TEXTURE_2D, 0, xoffset, yoffset, width, height, context.RGBA_INTEGER, context.UNSIGNED_INT, shared.covAndColorData, shared.loadedVertexCount * 4)
    shared.gl.resetState()
    shared.loadedVertexCount += width * height
    vertexCount -= width * height
  }
  return matrices
}

// ─── WebGPU migration Stage 2 (?webgpu=1) — geometry conversion ───────────────
//
// Rather than hand-write a TSL shader from scratch, this reuses three.js's
// own native Gaussian-splat renderer (three/addons/objects/GaussianSplat.js,
// merged r186 — the exact version this app is now pinned to), which already
// has a correct, tested covariance-projection + quad-expansion TSL vertex
// shader, a GPU compute-shader sort, AND full degree-1/2/3 SH support (the
// original migration-planning research said SH was unsupported there —
// that turned out to be stale; the shipped version has it). Adopting it
// directly is far lower-risk than re-deriving and re-verifying the same
// math a second time in TSL by hand.
//
// GaussianSplat's geometry format (three/addons/utils/GaussianSplatUtils.js,
// createGaussianSplatGeometry): centers as Float32Array xyz triples,
// covariances as Float32Array 6-element upper-triangles [c00,c01,c02,c11,
// c12,c22] per splat, colors as Uint8Array RGBA quads. This maps directly
// onto this app's own already-parsed data — shared.covAndColorData's packed
// int16 pairs decode to EXACTLY that same [M11,M12,M13,M22,M23,M33] upper-
// triangle order (see the GLSL vertex shader's Vrk construction above), so
// no re-derivation from source scale/rotation is needed, only unpacking.
// SH degree 1-3 is packed into the addon's band format by
// packSphericalHarmonicsForAddon (below).
function unpackCovInt16(value: number): [number, number] {
  // Exact port of the GLSL vertex shader's unpackInt16 — see its own
  // comment. JS's bitwise operators are already 32-bit signed (ToInt32),
  // so this is a direct, faithful translation, not an approximation.
  const v = value | 0
  const v0 = v >> 16
  let v1 = v & 0xFFFF
  if (v1 & 0x8000) v1 |= 0xFFFF0000
  return [v1, v0]
}

export async function buildGaussianSplatGeometry(shared: SharedState) {
  let lastYield = performance.now()
  const count = shared.numVertices
  const centers = new Float32Array(count * 3)
  const covariances = new Float32Array(count * 6)
  const colors = new Uint8Array(count * 4)
  const csData = shared.centerAndScaleData
  const ccData = shared.covAndColorData
  for (let i = 0; i < count; i++) {
    const scale = csData[i * 4 + 3]
    centers[i * 3] = csData[i * 4]
    centers[i * 3 + 1] = csData[i * 4 + 1]
    centers[i * 3 + 2] = csData[i * 4 + 2]

    const [m11, m12] = unpackCovInt16(ccData[i * 4])
    const [m13, m22] = unpackCovInt16(ccData[i * 4 + 1])
    const [m23, m33] = unpackCovInt16(ccData[i * 4 + 2])
    covariances[i * 6] = m11 * scale
    covariances[i * 6 + 1] = m12 * scale
    covariances[i * 6 + 2] = m13 * scale
    covariances[i * 6 + 3] = m22 * scale
    covariances[i * 6 + 4] = m23 * scale
    covariances[i * 6 + 5] = m33 * scale

    const colorUint = ccData[i * 4 + 3]
    colors[i * 4] = colorUint & 0xFF
    colors[i * 4 + 1] = (colorUint >>> 8) & 0xFF
    colors[i * 4 + 2] = (colorUint >>> 16) & 0xFF
    colors[i * 4 + 3] = colorUint >>> 24
    if ((i & 4095) === 0 && performance.now() - lastYield > 30) {
      shared.onStageProgress?.('build', (shared.hasSH ? 0.3 : 1) * i / count)
      await yieldToMain()
      lastYield = performance.now()
    }
  }
  const sphericalHarmonics = shared.hasSH && shared.shData
    ? await packSphericalHarmonicsForAddon(shared, count)
    : {}
  return createGaussianSplatGeometry(centers, covariances, colors, sphericalHarmonics)
}

// ─── SH for the addon ────────────────────────────────────────────────────────
// Converts our fp16 SH texture rows (setSHRow layout: per channel
// [dc, f0, f1, f2], [f3..f6], … — 1/3/4 rows per channel for degree 1/2/3)
// into the addon's packed bands: per band, basis-major (r, g, b) bytes,
// four per uint32 word, byte = coef * 128 + 128 (clamped).
//
// Two corrections, so the result matches the WebGL path:
//  - Axis flip: splats are stored as (x, -y, -z) (see pushSOGData). The GLSL
//    flips the view direction back before evaluating SH; the addon evaluates
//    with the mesh-space direction. Basis functions odd in y XOR z change
//    sign under that flip, so their coefficients are negated (SH_FLIP_SIGN,
//    in 3DGS f_rest order: band 1 [y, z, x], band 2 [xy, yz, 2zz-xx-yy, xz,
//    xx-yy], band 3 [y(3xx-yy), xyz, y(4zz-xx-yy), z(2zz-3xx-3yy),
//    x(4zz-xx-yy), z(xx-yy), x(xx-3yy)]).
//  - Colour space: the GLSL treats SH output as sRGB and linearizes the sum;
//    the addon adds SH straight onto the (linear) base colour. Each
//    coefficient is scaled by d(linear)/d(sRGB) at that splat's own base
//    colour — a first-order match, exact for the base colour itself.
const SH_FLIP_SIGN = [-1, -1, 1, -1, 1, 1, -1, 1, -1, 1, -1, -1, 1, -1, 1]
const SH_BAND_FIRST = [0, 0, 3, 8] // first f_rest index of band 1/2/3
const SH_BAND_BASIS = [0, 3, 5, 7]
let halfToFloatLUT: Float32Array | null = null

function srgbToLinearSlope(s: number): number {
  if (s <= 0.04045) return 1 / 12.92
  return (2.4 / 1.055) * Math.pow((s + 0.055) / 1.055, 1.4)
}

async function packSphericalHarmonicsForAddon(shared: SharedState, count: number) {
  if (!halfToFloatLUT) {
    halfToFloatLUT = new Float32Array(65536)
    for (let h = 0; h < 65536; h++) halfToFloatLUT[h] = THREE.DataUtils.fromHalfFloat(h)
  }
  const lut = halfToFloatLUT
  const shData = shared.shData!
  const W = shared.bufferTextureWidth
  const degree = shared.shDegree
  const shRows = shRowsForDegree(degree)
  const rowsPerChannel = shRows / 3
  const bands: { packed: Uint32Array; bytes: Uint8ClampedArray }[] = []
  for (let b = 1; b <= degree; b++) bands[b] = createPackedSphericalHarmonicsBand(count, b)
  const SH_C0 = 0.28209479177387814
  const slope = [0, 0, 0]
  let lastYield = performance.now()
  for (let i = 0; i < count; i++) {
    if ((i & 4095) === 0 && performance.now() - lastYield > 30) {
      shared.onStageProgress?.('build', 0.3 + 0.7 * i / count)
      await yieldToMain()
      lastYield = performance.now()
    }
    const col = i % W
    const rowBase = Math.floor(i / W) * shRows
    // Coefficient p (0 = dc, 1.. = f_rest) of channel c.
    const coef = (c: number, p: number) =>
      lut[shData[((rowBase + c * rowsPerChannel + (p >> 2)) * W + col) * 4 + (p & 3)]]
    for (let c = 0; c < 3; c++) {
      const s = Math.min(1, Math.max(0, 0.5 + SH_C0 * coef(c, 0)))
      slope[c] = srgbToLinearSlope(s)
    }
    for (let b = 1; b <= degree; b++) {
      const { bytes } = bands[b]
      const words = bytes.length / count / 4
      const base = i * words * 4
      for (let k = 0; k < SH_BAND_BASIS[b]; k++) {
        const j = SH_BAND_FIRST[b] + k
        const sign = SH_FLIP_SIGN[j]
        for (let c = 0; c < 3; c++) {
          bytes[base + k * 3 + c] = coef(c, j + 1) * sign * slope[c] * 128 + 128
        }
      }
    }
  }
  const out: Record<string, Uint32Array> = {}
  for (let b = 1; b <= degree; b++) out[`sh${b}`] = bands[b].packed
  return out
}

// Single entry point for the WebGPU path — mirrors `new Splat(shared, ...)`
// closely enough for SceneHandler.js to swap one for the other behind the
// ?webgpu=1 check without needing to know about geometry conversion itself.
// No SH wiring yet (Stage 2 scope is flat SH0 color only); no camera/
// covClamp/alphaHash params either — GaussianSplat doesn't use them
// (covClamp has no TSL equivalent yet, alphaHash mode doesn't apply since
// this always depth-tests, camera is read via the built-in `cameraViewport`/
// `cameraProjectionMatrix` TSL nodes instead of a manually-threaded uniform).
//
// MUST wait for triggerLazyLoad to genuinely finish before building the
// geometry — confirmed live: building it immediately (matching how the GLSL
// path's connect() appears to, at a glance) silently produced an all-zero
// mesh forever, because GaussianSplat's BufferGeometry is a one-time
// snapshot of shared's typed arrays, unlike the GLSL path's DataTexture,
// which mutates in place and re-uploads on next render regardless of when
// its backing array was actually populated. Async, so the caller awaits it.
export async function createWebGPUSplatMesh(shared: SharedState) {
  const promise = triggerLazyLoad(shared)
  if (promise) {
    await promise
  } else {
    // Something else already kicked off loading (shared.loading was
    // already true) — no promise to await, so fall back to polling the
    // same genuine-completion flag the rest of this file trusts.
    while (!shared.loaded) await new Promise((r) => setTimeout(r, 20))
  }
  const geometry = await buildGaussianSplatGeometry(shared)
  // Now safe to drop the CPU-side staging arrays — see uploadAllTextures'
  // WebGPU branch for why it deliberately doesn't do this itself.
  freeStagingArrays(shared)
  const mesh = new GaussianSplat(geometry)
  // SceneHandler renders WebGPU with a linear output colour space (so our
  // renderer blends in sRGB like WebGL) — encode the addon's colour too.
  const original = (mesh.material as any).colorNode
  ;(mesh.material as any).colorNode = tslVec4(sRGBTransferOETF(original.rgb), original.a)
  return mesh
}

// ─── Splat mesh class ─────────────────────────────────────────────────────────

export class Splat extends THREE.Mesh {
  constructor(shared: SharedState, camera: THREE.Camera, { toneMapped = false, alphaTest = 0, alphaHash = false, covClamp = 0.1 } = {}) {
    super()
    this.frustumCulled = false
    this.onBeforeRender = () => shared.update(this as unknown as TargetMesh, camera, alphaHash)
    if ((shared.gl as any).isWebGPURenderer) {
      // WebGPU: TSL port of SplatMaterial (three/splatNodeMaterial.js) +
      // GPU sort (three/splatGpuSort.js) + indirect draw of only the visible
      // splats. Blends in sRGB space (srgbOutput) — SceneHandler sets the
      // renderer's outputColorSpace to linear to match. No SH / alphaHash yet.
      shared.gpuSort = true
      // Streamed SOG: draw from the pool. Its blocks are scattered over the
      // whole capacity (buddy/exact allocator), so every slot is sorted and
      // empty or freed slots are skipped by their zero opacity (the pool
      // zeroes freed blocks on WebGPU); re-sort when the pool's contents
      // change (pool.version), not just the camera.
      const pool = (shared as any).pool
      const splatCount = pool ? pool.capacity : shared.numVertices
      if (pool) pool.initGpuTextures?.()
      // A/B switches for measuring (default on): ?quad4=0 → 6-vertex
      // non-indexed quad, ?opacityExtent=0 → full-size quads.
      const ab = new URLSearchParams(window.location.search)
      // ?cpuSort=1 (opt-in, 2026-10-23): streamed models sort on the CPU
      // (the WebGL path's streaming sort worker) with the order as an
      // instanced attribute. Tried as the default on an M4 iPad: the screen
      // dropped to ~3 fps with flickering splats while the page counted 35 —
      // reverted to the GPU sort; kept only for investigating.
      if (pool && ab.get('cpuSort') === '1') {
        shared.gpuSort = false
        const material = createSplatNodeMaterial({
          centerAndScaleTexture: shared.centerAndScaleTexture,
          covAndColorTexture: shared.covAndColorTexture,
          textureWidth: shared.bufferTextureWidth,
          covClamp,
          order: null, // per-instance splatIndex attribute (connect())
          srgbOutput: true,
          shTexture: shared.hasSH ? shared.shTexture : null,
          shDegree: shared.hasSH ? shared.shDegree : 0,
          opacityExtent: ab.get('opacityExtent') !== '0',
          vertexSrgb: ab.get('vsrgb') !== '0',
          fragCut: ab.get('fragCut') !== '0',
          packColor: ab.get('packColor') !== '0',
        })
        this.material = material
        shared.connect(this as unknown as TargetMesh)
        this.onBeforeRender = (_renderer: any, _scene: any, cam: THREE.Camera) => shared.update(this as unknown as TargetMesh, cam, false)
        return
      }
      const indexed = ab.get('quad4') !== '0'
      const dirThr = parseFloat(ab.get('sortDir') || '')
      const sort = createSplatGpuSort({
        indexed,
        // (2026-10-22: a "re-sort only past ~1.8°" default, sub-pixel cull,
        // used-top dispatch and one-pass batching were tried here and rolled
        // back — on an M4 iPad the picture dropped to a few fps while the
        // page still counted 45. ?sortDir=<cosine> still opts in to the first.)
        sortDirThreshold: Number.isFinite(dirThr) ? dirThr : null,
        // Measured one at a time (2026-10-25, M3 Pro, turning 30°/s):
        // reusing the last sort's depth range cut the sort's GPU time by a
        // third (Safari 2.7 → 1.8 ms, Chrome 2.7 → 1.8 ms; ?sortRange=full =
        // the old range pass). One submission (?sortBatch=1) and re-sorting
        // only past ~1.8° (?sortDir=0.9995 — streaming changes force a
        // re-sort anyway) showed no gain, so they stay opt-in.
        prevRange: ab.get('sortRange') !== 'full',
        batch: ab.get('sortBatch') === '1',
        count: splatCount,
        centerAndScaleTexture: shared.centerAndScaleTexture,
        covAndColorTexture: shared.covAndColorTexture,
        textureWidth: shared.bufferTextureWidth,
      })
      const material = createSplatNodeMaterial({
        centerAndScaleTexture: shared.centerAndScaleTexture,
        covAndColorTexture: shared.covAndColorTexture,
        textureWidth: shared.bufferTextureWidth,
        covClamp,
        order: sort.orderRead,
        srgbOutput: true,
        shTexture: shared.hasSH ? shared.shTexture : null,
        shDegree: shared.hasSH ? shared.shDegree : 0,
        opacityExtent: ab.get('opacityExtent') !== '0',
        vertexSrgb: ab.get('vsrgb') !== '0',
        fragCut: ab.get('fragCut') !== '0',
        packColor: ab.get('packColor') !== '0',
      })
      this.material = material
      const geometry = new THREE.InstancedBufferGeometry()
      if (indexed) {
        // 4 shared corners: each corner's vertex shader runs once, not
        // twice (same two counter-clockwise triangles as below).
        geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
          -2, -2, 0, 2, -2, 0, 2, 2, 0, -2, 2, 0,
        ]), 3))
        geometry.setIndex([0, 1, 2, 0, 2, 3])
      } else {
        geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
          -2, -2, 0, 2, 2, 0, -2, 2, 0,
          2, -2, 0, 2, 2, 0, -2, -2, 0,
        ]), 3))
      }
      geometry.instanceCount = splatCount
      geometry.setIndirect(sort.indirect as any)
      this.geometry = geometry
      const size = new THREE.Vector2()
      // CPU-side texture copies: three uploads them on the first frame that
      // uses the textures after shared.loaded; free them one frame later
      // (the WebGL path frees right after its own synchronous upload).
      let uploadFrame = false
      let freed = false
      this.onBeforeRender = (renderer: any, _scene: any, cam: THREE.Camera) => {
        renderer.getDrawingBufferSize(size)
        material.viewport.set(size.x, size.y)
        material.focal = (size.y / 2) * Math.abs(cam.projectionMatrix.elements[5])
        sort.update(renderer, cam, this, pool ? splatCount : shared.loadedVertexCount, pool ? pool.version : 0)
        if (!pool && !freed && shared.loaded) {
          if (uploadFrame) { freeStagingArrays(shared); freed = true }
          else uploadFrame = true
        }
      }
      triggerLazyLoad(shared)
      return
    }
    this.material = new SplatMaterial()
    Object.assign(this.material, {
      transparent: !alphaHash,
      depthTest: true,
      alphaTest: alphaHash ? 0 : alphaTest,
      centerAndScaleTexture: shared.centerAndScaleTexture,
      covAndColorTexture: shared.covAndColorTexture,
      hasSH: shared.hasSH ? 1 : 0,
      shDegree: shared.shDegree,
      shLayout: shared.shDegree,
      shTexture: shared.shTexture ?? null,
      covClamp,
      depthWrite: alphaHash ? true : alphaTest > 0,
      blending: alphaHash ? THREE.NormalBlending : THREE.CustomBlending,
      blendSrcAlpha: THREE.OneFactor,
      alphaHash: !!alphaHash,
      toneMapped: toneMapped
    })
    shared.connect(this as unknown as TargetMesh)
  }
}
